import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Building2,
  CalendarClock,
  Check,
  ChevronDown,
  Globe,
  HelpCircle,
  KeyRound,
  Plus,
  Save,
  Search,
  ShieldAlert,
  ShieldCheck,
  Store,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { SaaSServices } from '@/services/saasServices';
import { apiErrorMessage, readStoredInstitution, readStoredUser, unwrapApiData } from '@/auth/saasSession';
import {
  ACCESS_MODE_OPTIONS,
  LICENSE_FEATURE_OPTIONS,
} from '@/auth/saasCatalog';
import { defaultLicense, licensePayload, toLicenseForm } from './saasLicenseForm';
import {
  isGlobalMarketplaceSearch,
  SAAS_ADMINISTRATION_SECTION_EVENT,
  SAAS_GLOBAL_MARKETPLACE_SECTION,
  SAAS_ADMINISTRATION_TAB_DEFINITIONS,
} from './saasAdministrationNavigation';
import {
  BREAK_GLASS_HELP,
  BREAK_GLASS_HELP_AR,
  InfoTip,
  LICENSE_FIELD_HELP,
  LICENSE_FIELD_HELP_AR,
} from './SaaSInfoTip';
import {
  activeBreakGlassGrant,
  breakGlassExpiryLimits,
  isValidIanaTimezone,
  setLicenseFeature,
  supportGrantIsActive,
  validateBreakGlassExpiry,
} from './saasAdministrationForm';
import { COUNTRIES } from '../uitilites/countries';
import {
  formatInstitutionDateTime,
  IANA_TIMEZONE_OPTIONS,
  institutionLocalDateTimeToIso,
  resolvedInstitutionTimeZone,
} from '../uitilites/institutionTime';
import {
  readSaaSInstitutionSelection,
  SAAS_INSTITUTION_SELECTION_EVENT,
  selectSaaSInstitution,
} from '../auth/saasControlPlaneSelection';
import {
  dismissMarketplaceGuide,
  marketplaceGuideWasDismissed,
} from '../auth/marketplaceGuideSession';
import SaaSOperationsAreaMap from '../components/map/SaaSOperationsAreaMap';
import './SaaSAdministration.css';

const TAB_ICONS = {
  institutions: Building2,
  'license-features': KeyRound,
  support: HelpCircle,
};

const SAAS_ADMINISTRATION_TABS = SAAS_ADMINISTRATION_TAB_DEFINITIONS.map(([id, label]) => (
  [id, label, TAB_ICONS[id]]
));

const HelpLabel = ({ htmlFor, label, help }) => (
  <span className="saas-help-label">
    <label htmlFor={htmlFor}>{label}</label>
    <InfoTip label={label} content={help} />
  </span>
);

const Panel = ({ title, description, action, children }) => (
  <section className="saas-panel">
    <header className="saas-panel-header">
      <div><h2 className="saas-panel-title">{title}</h2>{description && <p className="saas-panel-description">{description}</p>}</div>
      {action}
    </header>
    <div className="saas-panel-body">{children}</div>
  </section>
);

const InstitutionTimezoneField = ({ id, value, onChange, label = 'Institution timezone', ar = false }) => {
  const optionsId = `${id}-iana-options`;
  return (
    <div className="saas-field">
      <label htmlFor={id}>{label}</label>
      <Input
        id={id}
        className="saas-input"
        required
        list={optionsId}
        placeholder={ar ? 'ابحث أو اكتب، مثال: Asia/Riyadh' : 'Search or type, for example Asia/Riyadh'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <datalist id={optionsId}>
        {IANA_TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone} />)}
      </datalist>
    </div>
  );
};

const OperationsCountryField = ({ id, value, onChange, ar = false }) => {
  const optionsId = `${id}-options`;
  const selectedCountry = COUNTRIES.find((country) => country.iso2 === value.country_iso2);
  const [query, setQuery] = useState(selectedCountry?.name || value.country_name || '');

  useEffect(() => {
    const nextValue = selectedCountry?.name || value.country_name || '';
    setQuery(nextValue);
  }, [selectedCountry?.name, value.country_name]);

  const updateCountry = (rawValue) => {
    setQuery(rawValue);
    const normalized = rawValue.trim().toLocaleLowerCase();
    const match = COUNTRIES.find((country) => (
      country.name.toLocaleLowerCase() === normalized
      || country.iso2.toLocaleLowerCase() === normalized
    ));
    onChange({
      ...value,
      country_iso2: match?.iso2 || '',
      country_name: match?.name || rawValue,
    });
  };

  return (
    <div className="saas-field saas-field-caps">
      <label htmlFor={id}>{ar ? 'الدولة' : 'COUNTRY'}</label>
      <Input
        id={id}
        className="saas-input"
        required
        list={optionsId}
        placeholder={ar ? 'ابحث أو اكتب اسم الدولة' : 'Search or type a country'}
        value={query}
        onChange={(event) => updateCountry(event.target.value)}
      />
      <datalist id={optionsId}>
        {COUNTRIES.map((country) => <option key={country.iso2} value={country.name}>{country.iso2}</option>)}
      </datalist>
    </div>
  );
};

const DEFAULT_OPERATIONS_AREA = {
  country_iso2: 'SA',
  country_name: 'Saudi Arabia',
  radius_km: 2200,
};

const operationsAreaFor = (institution) => ({
  ...DEFAULT_OPERATIONS_AREA,
  ...(institution?.operations_area || {}),
});

const INSTITUTION_STATUS_DEFINITIONS = [
  ['Active', 'Normal licensed access is available.'],
  ['Provisioning', 'Initial institution setup is still in progress.'],
  ['Grace', 'Temporary access follows the configured license grace behavior.'],
  ['Suspended', 'Institution access is blocked until the platform restores it.'],
  ['Closed', 'The institution is inactive and cannot be opened.'],
];

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

const remainingTimeLabel = (milliseconds, ar) => {
  const days = Math.ceil(milliseconds / DAY_MS);
  const hours = Math.ceil(milliseconds / HOUR_MS);
  if (milliseconds < DAY_MS) {
    if (hours <= 1) return ar ? 'أقل من ساعة' : 'Less than 1 hour';
    return ar ? `${hours} ساعة` : `${hours} hours`;
  }
  if (days === 1) return ar ? 'يوم واحد' : '1 day';
  return ar ? `${days} يومًا` : `${days} days`;
};

const subscriptionExpiryView = (institution, snapshot, ar, now = Date.now()) => {
  const license = snapshot?.license;
  const timezone = resolvedInstitutionTimeZone(institution?.timezone);
  if (!snapshot || !license || typeof license !== 'object') {
    return {
      state: 'unavailable',
      primary: ar ? 'غير متاح' : 'Unavailable',
      secondary: ar ? 'تعذر تحميل بيانات الترخيص' : 'License data could not be loaded',
      expiresAt: '',
      timezone,
      sortRank: 5,
      sortAt: Number.POSITIVE_INFINITY,
    };
  }

  const status = String(license.status || '').toLowerCase();
  const rawExpiry = String(status === 'grace' ? license.grace_ends_at || '' : license.expires_at || '').trim();
  const expiry = rawExpiry ? Date.parse(rawExpiry) : NaN;
  const formattedExpiry = Number.isFinite(expiry) ? formatInstitutionDateTime(expiry, timezone) : '';

  if (status === 'cancelled') {
    return {
      state: 'cancelled',
      primary: ar ? 'ملغى' : 'Cancelled',
      secondary: ar ? 'تم إنهاء الترخيص' : 'License was cancelled',
      expiresAt: formattedExpiry,
      timezone,
      sortRank: 3,
      sortAt: Number.isFinite(expiry) ? expiry : Number.POSITIVE_INFINITY,
    };
  }

  if (status === 'suspended') {
    return {
      state: 'suspended',
      primary: ar ? 'موقوف' : 'Suspended',
      secondary: ar ? 'الوصول موقوف' : 'Access is paused',
      expiresAt: formattedExpiry,
      timezone,
      sortRank: 3,
      sortAt: Number.isFinite(expiry) ? expiry : Number.POSITIVE_INFINITY,
    };
  }

  if (status === 'expired' || (Number.isFinite(expiry) && expiry <= now)) {
    return {
      state: 'expired',
      primary: ar ? 'منتهي' : 'Expired',
      secondary: Number.isFinite(expiry)
        ? (ar ? 'انتهى الترخيص' : 'License has ended')
        : (ar ? 'الترخيص غير نشط' : 'License is no longer active'),
      expiresAt: formattedExpiry,
      timezone,
      sortRank: 0,
      sortAt: Number.isFinite(expiry) ? expiry : Number.NEGATIVE_INFINITY,
    };
  }

  if (!rawExpiry) {
    return {
      state: 'no-expiration',
      primary: ar ? 'بلا انتهاء' : 'No expiration',
      secondary: ar ? 'لم يُحدد تاريخ انتهاء' : 'No expiration date is configured',
      expiresAt: '',
      timezone,
      sortRank: 4,
      sortAt: Number.POSITIVE_INFINITY,
    };
  }

  if (!Number.isFinite(expiry)) {
    return {
      state: 'unavailable',
      primary: ar ? 'غير متاح' : 'Unavailable',
      secondary: ar ? 'تاريخ انتهاء الترخيص غير صالح' : 'License expiration date is invalid',
      expiresAt: '',
      timezone,
      sortRank: 5,
      sortAt: Number.POSITIVE_INFINITY,
    };
  }

  const remaining = expiry - now;
  return {
    state: remaining <= 30 * DAY_MS ? 'expiring' : 'active',
    primary: remainingTimeLabel(remaining, ar),
    secondary: status === 'grace'
      ? (ar ? 'متبقي من فترة السماح' : 'grace remaining')
      : (ar ? 'متبقي' : 'remaining'),
    expiresAt: formattedExpiry,
    timezone,
    sortRank: remaining <= 30 * DAY_MS ? 1 : 2,
    sortAt: expiry,
  };
};

const OperationsAreaFields = ({ value, onChange, onSave, saving, idPrefix, className = '', ar = false }) => {
  const selectedCountry = COUNTRIES.find((country) => country.iso2 === value.country_iso2);
  const radius = Math.min(5000, Math.max(100, Number(value.radius_km || 2200)));
  return (
    <div className={`saas-operations ${className}`}>
      <div className="saas-operations-header">
        <span className="saas-icon-box"><Globe aria-hidden="true" /></span>
        <div className="saas-operations-copy">
          <p>{ar ? 'منطقة العمليات' : 'Operations Area'}</p>
          <span>{ar ? 'نشطة' : 'Active'}: {selectedCountry?.name || value.country_name || (ar ? 'الدولة' : 'Country')} · {radius.toLocaleString()} {ar ? 'كم نطاق' : 'km radius'}</span>
        </div>
        {onSave && <button type="button" className="saas-button saas-button-sky" onClick={onSave} disabled={saving || !selectedCountry}><Save aria-hidden="true" /> {ar ? 'حفظ المنطقة' : 'Save area'}</button>}
      </div>
      <div className="saas-operations-grid">
        <OperationsCountryField
          id={`${idPrefix}-country`}
          value={value}
          onChange={onChange}
          ar={ar}
        />
        <label className="saas-radius-field" htmlFor={`${idPrefix}-radius`}>
          <span className="saas-radius-label"><strong>{ar ? 'نطاق المراقبة' : 'WATCH RADIUS'}</strong><b>{radius.toLocaleString()} {ar ? 'كم' : 'km'}</b></span>
          <input
            id={`${idPrefix}-radius`}
            type="range"
            min="100"
            max="5000"
            step="100"
            value={radius}
            aria-valuetext={ar ? `${radius.toLocaleString()} كيلومتر` : `${radius.toLocaleString()} kilometres`}
            onChange={(event) => onChange({ ...value, radius_km: Number(event.target.value) })}
            className="saas-range"
          />
          <span className="saas-range-limits"><span>100 {ar ? 'كم' : 'km'}</span><span>5,000 {ar ? 'كم' : 'km'}</span></span>
        </label>
        <div className="saas-map-preview">
          <SaaSOperationsAreaMap
            countryIso2={value.country_iso2}
            countryName={selectedCountry?.name || value.country_name}
            radiusKm={radius}
          />
        </div>
      </div>
    </div>
  );
};

export const LicenseEditor = ({ institution, value, seatUsage, effectiveMode, onChange, onSave, saving, ar = false }) => {
  const licenseHelp = ar ? LICENSE_FIELD_HELP_AR : LICENSE_FIELD_HELP;
  const apiConnectionsEnabled = value.feature_entitlements?.api_connections !== false;
  const licensedSeats = Number(value.licensed_total_seats || 0);
  const usedSeats = Number(seatUsage?.named_users || 0);
  const seatsNearLimit = licensedSeats > 0 && (usedSeats / licensedSeats) >= 0.9;
  return <Panel title={`${ar ? 'الترخيص' : 'License'} · ${institution.display_name}`} description={ar ? 'يغطي مقعد المستخدم الواحد الشخص نفسه على الويب أو الهاتف أو كليهما.' : 'One named-user seat covers the same person on web, mobile, or both. Web and mobile access are enable flags, not separate seat pools.'} action={<button type="button" className="saas-button saas-button-primary" onClick={onSave} disabled={saving}><Save aria-hidden="true" /> {ar ? 'حفظ الترخيص' : 'Save license'}</button>}>
    <div className="saas-license-stats">
      <div className={`saas-dark-stat ${seatsNearLimit ? 'is-near-limit' : ''}`}><strong>{usedSeats}/{licensedSeats || 0}</strong><span>{ar ? 'المقاعد المستخدمة' : 'Named seats used'}</span><i><b style={{ width: `${licensedSeats ? Math.min(100, (usedSeats / licensedSeats) * 100) : 0}%` }} /></i></div>
      <div className="saas-dark-stat"><strong>{seatUsage?.web_enabled_users || 0}</strong><span>{ar ? 'الويب مفعّل' : 'Web enabled'}</span></div>
      <div className="saas-dark-stat"><strong className="saas-capitalize">{ar ? ({ full: 'كامل', operational_only: 'تشغيلي فقط', read_only: 'قراءة فقط', deny: 'مرفوض', unknown: 'غير معروف' }[effectiveMode || 'unknown'] || String(effectiveMode || 'unknown').replace(/_/g, ' ')) : String(effectiveMode || 'unknown').replace(/_/g, ' ')}</strong><span>{ar ? 'وضع الوصول الفعلي' : 'Effective access mode'}</span></div>
    </div>
    <SectionRule label={ar ? 'شروط الترخيص' : 'LICENSE TERMS'} />
    <div className="saas-fields-grid">
      <div className="saas-field"><HelpLabel htmlFor="license-status" label={ar ? 'الحالة' : 'Status'} help={licenseHelp.status} /><select id="license-status" value={value.status} onChange={(event) => onChange({ ...value, status: event.target.value })} className="saas-select"><option value="trial">{ar ? 'تجريبي' : 'Trial'}</option><option value="active">{ar ? 'نشط' : 'Active'}</option><option value="grace">{ar ? 'فترة سماح' : 'Grace'}</option><option value="suspended">{ar ? 'موقوف' : 'Suspended'}</option><option value="expired">{ar ? 'منتهي' : 'Expired'}</option><option value="cancelled">{ar ? 'ملغى' : 'Cancelled'}</option></select></div>
      <div className="saas-field"><HelpLabel htmlFor="license-starts" label={ar ? 'يبدأ' : 'Starts'} help={licenseHelp.starts} /><Input id="license-starts" className="saas-input" type="datetime-local" value={value.starts_at} onChange={(event) => onChange({ ...value, starts_at: event.target.value })} /></div>
      <div className="saas-field"><HelpLabel htmlFor="license-expires" label={ar ? 'ينتهي' : 'Expires'} help={licenseHelp.expires} /><Input id="license-expires" className="saas-input" type="datetime-local" value={value.expires_at} onChange={(event) => onChange({ ...value, expires_at: event.target.value })} /></div>
      <div className="saas-field"><HelpLabel htmlFor="license-grace-ends" label={ar ? 'نهاية فترة السماح' : 'Grace ends'} help={licenseHelp.graceEnds} /><Input id="license-grace-ends" className="saas-input" type="datetime-local" value={value.grace_ends_at || ''} onChange={(event) => onChange({ ...value, grace_ends_at: event.target.value })} /></div>
      <div className="saas-field"><HelpLabel htmlFor="license-seats" label={ar ? 'مقاعد المستخدمين' : 'Named-user seats'} help={licenseHelp.seats} /><Input id="license-seats" className="saas-input" type="number" min="0" value={value.licensed_total_seats} onChange={(event) => onChange({ ...value, licensed_total_seats: event.target.value })} /></div>
      <div className="saas-field"><HelpLabel htmlFor="license-grace-behavior" label={ar ? 'سلوك فترة السماح' : 'Grace behavior'} help={licenseHelp.graceBehavior} /><select id="license-grace-behavior" value={value.grace_access_mode} onChange={(event) => onChange({ ...value, grace_access_mode: event.target.value })} className="saas-select">{ACCESS_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{ar ? ({ full: 'وصول كامل', operational_only: 'تشغيلي فقط', read_only: 'قراءة فقط', deny: 'منع الدخول' }[option.value] || option.label) : option.label}</option>)}</select></div>
      <div className="saas-field"><HelpLabel htmlFor="license-expired-behavior" label={ar ? 'سلوك انتهاء الترخيص' : 'Expired behavior'} help={licenseHelp.expiredBehavior} /><select id="license-expired-behavior" value={value.expired_access_mode} onChange={(event) => onChange({ ...value, expired_access_mode: event.target.value })} className="saas-select">{ACCESS_MODE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{ar ? ({ full: 'وصول كامل', operational_only: 'تشغيلي فقط', read_only: 'قراءة فقط', deny: 'منع الدخول' }[option.value] || option.label) : option.label}</option>)}</select></div>
    </div>
    <div>
      <SectionRule label={ar ? 'المزايا المرخّصة' : 'FEATURE ENTITLEMENTS'} />
      <div className="saas-feature-grid">
        {LICENSE_FEATURE_OPTIONS.map((feature) => {
          const foresightBlocked = feature.value === 'foresight' && !apiConnectionsEnabled;
          const featureLabel = ar ? ({ reports: 'التقارير', iot: 'إنترنت الأشياء', api_connections: 'اتصالات API', foresight: 'ForeSight', scenario_marketplace: 'المتجر', partners: 'الشركاء' }[feature.value] || feature.label) : feature.label;
          return <label key={feature.value} className={`saas-feature-tile ${foresightBlocked ? 'is-disabled' : ''}`}><span>{featureLabel}{foresightBlocked && <small>{ar ? 'يتطلب اتصالات API' : 'Requires API connections'}</small>}</span><Switch className="saas-switch" aria-label={ar ? `ترخيص ميزة ${featureLabel}` : `${feature.label} entitlement`} disabled={foresightBlocked} checked={!foresightBlocked && value.feature_entitlements?.[feature.value] === true} onCheckedChange={(checked) => onChange(setLicenseFeature(value, feature.value, checked))} /></label>;
        })}
      </div>
    </div>
  </Panel>;
};

const SectionRule = ({ label, count, danger = false }) => (
  <div className={`saas-section-rule ${danger ? 'is-danger' : ''}`}>
    <h4>{label}</h4>
    {count !== undefined && <span>{count}</span>}
    <i />
  </div>
);

const WORKFLOW_STAGES = [
  { actor: 'SAAS ADMIN', color: 'sky', title: 'SaaS enables Marketplace globally', body: 'Choose Disabled, Selected publishing institutions, or All eligible institutions, and decide whether every submission requires approval.' },
  { actor: 'SAAS ADMIN', color: 'sky', title: 'SaaS enables the feature per institution', body: 'In License & Features, enable Scenario Marketplace for the institution.', note: 'Both the global policy and the institution entitlement must permit the action.' },
  { actor: 'INSTITUTION ADMIN', color: 'yellow', title: 'Institution creates a private scenario', body: 'The institution builds the scenario in its private Scenario Library. It remains private unless explicitly submitted.' },
  { actor: 'INSTITUTION ADMIN', color: 'yellow', title: 'Institution submits it', body: 'Publish to Marketplace adds a reviewer note and DisasterMS creates a sanitized snapshot.', removed: ['Live incidents', 'Chats or reports', 'Attachments', 'Locations & departments', 'Explicit user assignments', 'Runtime exercise cards'] },
  { actor: 'SAAS ADMIN', color: 'sky', title: 'Submission enters review', body: 'The submission starts Pending. SaaS can Approve, Reject, Suspend, and add a review note.' },
  { actor: 'DISASTERMS', color: 'green', title: 'Approved template becomes discoverable', body: 'Only Published templates appear. Eligible institutions can search by name, description, or publishing institution.' },
  { actor: 'INSTITUTION ADMIN', color: 'yellow', title: 'Another institution imports it', body: 'Import private copy creates a new Draft owned by the receiving institution.', added: ['A new scenario ID', 'Its own private ownership', 'Cleared departments, locations & staffing', 'Copied reusable timeline templates'], note: 'Future changes to the published template do not update the imported copy.' },
  { actor: 'SAAS ADMIN', color: 'sky', title: 'Publication can be withdrawn', body: 'The publisher can revoke its publication, and SaaS can reject or suspend it.', note: 'Previously imported private copies remain inside the receiving institutions.' },
];

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const ARABIC_TEXT = {
  overview: 'إدارة المنصة', marketplace: 'المتجر', provision: 'إضافة مؤسسة',
  platformControl: 'لوحة تحكم المنصة', saasTitle: 'إدارة منصة DisasterMS',
  saasDescription: 'دورة حياة المؤسسات والتراخيص والوصول إلى المزايا وحوكمة المتجر.',
  institutions: 'المؤسسات', seatsUsed: 'المقاعد المستخدمة', licensesExpiring: 'تراخيص قاربت على الانتهاء',
  pendingInvitations: 'دعوات معلقة', allActive: 'كلها نشطة', acrossLicenses: 'عبر جميع التراخيص',
  withinThirtyDays: 'خلال 30 يومًا', awaitingActivation: 'بانتظار التفعيل',
  seatUsage: 'استخدام المقاعد حسب المؤسسة', subscriptionRemaining: 'المدة المتبقية للاشتراكات',
  institutionTab: 'المؤسسة', licenseTab: 'الترخيص والمزايا', supportTab: 'الدعم',
  institutionLifecycle: 'دورة حياة المؤسسة', saveChanges: 'حفظ التغييرات', displayName: 'اسم العرض',
  status: 'الحالة', timezone: 'المنطقة الزمنية للمؤسسة', operationsArea: 'منطقة العمليات',
  saveArea: 'حفظ المنطقة', country: 'الدولة', radius: 'نطاق المراقبة',
  supportRequests: 'طلبات دعم المنصة', requests: 'الطلبات', noRequests: 'لا توجد طلبات دعم من هذه المؤسسة.',
  emergencySupport: 'تفويض طارئ للدعم', requestDetails: 'تفاصيل الطلب', activeAccess: 'وصول نشط',
  activateBreakGlass: 'تفعيل التفويض الطارئ', revokeNow: 'إلغاء الآن', startEmergency: 'بدء جلسة الطوارئ',
  saveLicense: 'حفظ الترخيص', marketplacePolicy: 'سياسة الوصول إلى المتجر', savePolicy: 'حفظ السياسة',
  publicationReview: 'مراجعة النشر', workflow: 'مسار عمل المتجر بالتفصيل', marketplaceSharing: 'كيف تعمل مشاركة المتجر',
  globalScope: 'نطاق عالمي · جميع المؤسسات', policySettings: 'إعدادات السياسة', submissions: 'المشاركات',
  openReadOnly: 'فتح دعم للقراءة فقط', handleReadOnly: 'معالجة بوضع القراءة فقط', closeCase: 'إغلاق حالة الدعم',
  next: 'التالي', back: 'السابق', cancel: 'إلغاء', provisionInstitution: 'إنشاء المؤسسة',
  loading: 'جارٍ تحميل إدارة المنصة…', retry: 'إعادة المحاولة', partialLoad: 'تعذر تحميل بعض بيانات المنصة.',
  noRecentActivity: 'لا يوجد نشاط حديث موثّق لعرضه.', invitationUnavailable: 'عدد الدعوات غير متاح',
  summaryLabel: 'ملخص المنصة', administrationSections: 'أقسام إدارة المؤسسة', marketplaceScope: 'نطاق المتجر',
  marketplaceSummary: 'ملخص المتجر', provisioningSteps: 'خطوات إنشاء المؤسسة',
};

const ARABIC_WORKFLOW_TITLES = [
  'تفعيل المتجر على مستوى المنصة', 'تفعيل الميزة لكل مؤسسة', 'إنشاء سيناريو خاص', 'إرسال السيناريو',
  'دخول المشاركة إلى المراجعة', 'إتاحة القالب المعتمد', 'استيراد نسخة خاصة', 'سحب النشر',
];

const ARABIC_WORKFLOW_ACTORS = [
  'مسؤول المنصة', 'مسؤول المنصة', 'مسؤول المؤسسة', 'مسؤول المؤسسة',
  'مسؤول المنصة', 'DisasterMS', 'مسؤول المؤسسة', 'مسؤول المنصة',
];

const ARABIC_WORKFLOW_BODIES = [
  'اختر التعطيل أو مؤسسات نشر محددة أو جميع المؤسسات المؤهلة، وحدد ما إذا كانت كل مشاركة تتطلب موافقة.',
  'فعّل ميزة متجر السيناريوهات للمؤسسة من الترخيص والمزايا.',
  'تبني المؤسسة السيناريو في مكتبة السيناريوهات الخاصة بها. ويبقى خاصًا ما لم يُرسل صراحةً.',
  'يضيف النشر في المتجر ملاحظة للمراجع، وينشئ DisasterMS لقطة منقحة.',
  'تبدأ المشاركة بحالة بانتظار المراجعة. يمكن للمنصة اعتمادها أو رفضها أو إيقافها مع إضافة ملاحظة.',
  'تظهر فقط القوالب المنشورة. ويمكن للمؤسسات المؤهلة البحث بالاسم أو الوصف أو المؤسسة الناشرة.',
  'ينشئ استيراد نسخة خاصة مسودة جديدة تملكها المؤسسة المستلمة.',
  'يمكن للناشر سحب النشر، ويمكن للمنصة رفضه أو إيقافه.',
];

const ARABIC_WORKFLOW_REMOVED = ['الحوادث المباشرة', 'المحادثات أو التقارير', 'المرفقات', 'المواقع والأقسام', 'تعيينات المستخدمين الصريحة', 'بطاقات التمرين وقت التشغيل'];
const ARABIC_WORKFLOW_ADDED = ['معرّف سيناريو جديد', 'ملكية خاصة مستقلة', 'أقسام ومواقع وتوظيف ممسوحة', 'قوالب خط زمني قابلة لإعادة الاستخدام'];
const ARABIC_WORKFLOW_NOTES = {
  1: 'يجب أن تسمح السياسة العالمية وترخيص المؤسسة بالإجراء معًا.',
  6: 'لا تُحدّث تغييرات القالب المنشور النسخة المستوردة لاحقًا.',
  7: 'تبقى النسخ الخاصة المستوردة سابقًا داخل المؤسسات المستلمة.',
};

export default function SaaSAdministration() {
  const [tab, setTab] = useState(() => {
    if (typeof window === 'undefined') return 'institutions';
    const requested = new URLSearchParams(window.location.search).get('tab');
    return SAAS_ADMINISTRATION_TABS.some(([id]) => id === requested) ? requested : 'institutions';
  });
  const [globalMarketplaceOpen, setGlobalMarketplaceOpen] = useState(() => (
    typeof window !== 'undefined' && isGlobalMarketplaceSearch(window.location.search)
  ));
  const [overviewOpen, setOverviewOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const search = new URLSearchParams(window.location.search);
    return !search.get('institution') && !isGlobalMarketplaceSearch(window.location.search);
  });
  const [workflowOpen, setWorkflowOpen] = useState(true);
  const [marketplaceGuideDismissed, setMarketplaceGuideDismissed] = useState(marketplaceGuideWasDismissed);
  const [provisionStep, setProvisionStep] = useState(1);
  const [language, setLanguage] = useState(() => (
    typeof window !== 'undefined' ? localStorage.getItem(SAAS_LANGUAGE_KEY) || 'en' : 'en'
  ));
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [licenseForm, setLicenseForm] = useState(defaultLicense());
  const [seatUsage, setSeatUsage] = useState({});
  const [effectiveMode, setEffectiveMode] = useState('');
  const [institutionLicenseSnapshots, setInstitutionLicenseSnapshots] = useState({});
  const [marketplacePolicy, setMarketplacePolicy] = useState({ sharing_mode: 'disabled', allowed_institution_ids: [], require_superadmin_approval: true, version: 0 });
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [marketplaceSearch, setMarketplaceSearch] = useState('');
  const [marketplaceStatus, setMarketplaceStatus] = useState('all');
  const [marketplaceInstitutionSearch, setMarketplaceInstitutionSearch] = useState('');
  const [supportUsers, setSupportUsers] = useState([]);
  const [supportRequests, setSupportRequests] = useState([]);
  const [supportAssignments, setSupportAssignments] = useState({});
  const [breakGlassGrant, setBreakGlassGrant] = useState({ support_user_id: '', reason: '', expires_at: '' });
  const [activeBreakGlass, setActiveBreakGlass] = useState(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [pendingSupportSession, setPendingSupportSession] = useState(null);
  const [pendingCaseClosure, setPendingCaseClosure] = useState(null);
  const [caseResolution, setCaseResolution] = useState('');
  const [pendingMarketplaceReview, setPendingMarketplaceReview] = useState(null);
  const [marketplaceReviewNote, setMarketplaceReviewNote] = useState('');
  const [pendingBreakGlassActivation, setPendingBreakGlassActivation] = useState(null);
  const [provision, setProvision] = useState({
    slug: '',
    legal_name: '',
    display_name: '',
    timezone: 'Asia/Riyadh',
    default_language: 'en',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    seats: 10,
    operations_area: { country_iso2: '', country_name: '', radius_km: 1250 },
  });

  const ar = language === 'ar';
  const tr = (key, fallback) => (ar ? ARABIC_TEXT[key] || fallback : fallback);

  const selectedInstitution = useMemo(() => institutions.find((row) => row.id === selectedId), [institutions, selectedId]);
  const selectedSupportRequests = useMemo(
    () => supportRequests.filter((row) => String(row.institution_id) === String(selectedId)),
    [supportRequests, selectedId],
  );
  const marketplaceSummary = useMemo(() => ({
    pending: marketplaceListings.filter((row) => row.status === 'pending').length,
    published: marketplaceListings.filter((row) => row.status === 'published').length,
    paused: marketplaceListings.filter((row) => ['rejected', 'suspended'].includes(row.status)).length,
  }), [marketplaceListings]);
  const filteredMarketplaceListings = useMemo(() => {
    const query = marketplaceSearch.trim().toLocaleLowerCase();
    return marketplaceListings.filter((listing) => {
      if (marketplaceStatus !== 'all' && listing.status !== marketplaceStatus) return false;
      if (!query) return true;
      return [listing.name, listing.source_institution_name, listing.publisher_note, listing.review_note]
        .some((value) => String(value || '').toLocaleLowerCase().includes(query));
    });
  }, [marketplaceListings, marketplaceSearch, marketplaceStatus]);
  const filteredMarketplaceInstitutions = useMemo(() => {
    const query = marketplaceInstitutionSearch.trim().toLocaleLowerCase();
    if (!query) return institutions;
    return institutions.filter((institution) => [institution.display_name, institution.legal_name, institution.slug]
      .some((value) => String(value || '').toLocaleLowerCase().includes(query)));
  }, [institutions, marketplaceInstitutionSearch]);
  const marketplaceAllowedCount = marketplacePolicy.sharing_mode === 'all'
    ? institutions.length
    : marketplacePolicy.sharing_mode === 'allowlist'
      ? (marketplacePolicy.allowed_institution_ids || []).length
      : 0;
  const currentSaaSUser = readStoredUser() || {};
  const currentSaaSUserId = String(currentSaaSUser.id || currentSaaSUser._id || '');
  const selectedInstitutionTimezone = resolvedInstitutionTimeZone(selectedInstitution?.timezone);
  const breakGlassLimits = breakGlassExpiryLimits(new Date(), selectedInstitutionTimezone);
  const provisionOperationsRadius = Number(provision.operations_area?.radius_km);
  const provisionOperationsValid = Boolean(
    provision.operations_area?.country_iso2
    && Number.isFinite(provisionOperationsRadius)
    && provisionOperationsRadius >= 100
    && provisionOperationsRadius <= 5000,
  );

  const overviewStats = useMemo(() => {
    const snapshots = Object.values(institutionLicenseSnapshots);
    const seatsUsed = snapshots.reduce((total, snapshot) => total + Number(snapshot?.seat_usage?.named_users || 0), 0);
    const seatsLicensed = snapshots.reduce((total, snapshot) => total + Number(snapshot?.license?.licensed_total_seats || 0), 0);
    const now = Date.now();
    const expiresSoon = snapshots.filter((snapshot) => {
      const expiry = Date.parse(snapshot?.license?.expires_at || '');
      return Number.isFinite(expiry) && expiry >= now && expiry <= now + (30 * 24 * 60 * 60 * 1000);
    }).length;
    const invitationCounts = institutions.map((institution) => {
      const snapshot = institutionLicenseSnapshots[institution.id] || {};
      const candidates = [
        institution.pending_invitations,
        institution.pending_invitation_count,
        institution.membership_summary?.pending_invitations,
        snapshot.pending_invitations,
        snapshot.membership_summary?.pending_invitations,
      ];
      const known = candidates.find((value) => Number.isFinite(Number(value)));
      return known === undefined ? null : Number(known);
    });
    const invitationsKnown = invitationCounts.length > 0 && invitationCounts.every((value) => value !== null);
    const invitations = invitationsKnown ? invitationCounts.reduce((total, value) => total + value, 0) : null;
    const statuses = institutions.reduce((counts, institution) => {
      const status = String(institution.status || 'unknown').toLowerCase();
      counts[status] = (counts[status] || 0) + 1;
      return counts;
    }, {});
    const licensedSnapshots = snapshots.filter((snapshot) => Number(snapshot?.license?.licensed_total_seats || 0) > 0);
    const nearCapacityInstitutions = licensedSnapshots.filter((snapshot) => {
      const used = Number(snapshot?.seat_usage?.named_users || 0);
      const total = Number(snapshot?.license?.licensed_total_seats || 0);
      return total > 0 && (used / total) >= 0.9;
    }).length;
    const availableSeats = licensedSnapshots.reduce((total, snapshot) => {
      const used = Number(snapshot?.seat_usage?.named_users || 0);
      const licensed = Number(snapshot?.license?.licensed_total_seats || 0);
      return total + Math.max(0, licensed - used);
    }, 0);
    return {
      seatsUsed,
      seatsLicensed,
      expiresSoon,
      invitations,
      statuses,
      availableSeats,
      nearCapacityInstitutions,
      capacityKnown: licensedSnapshots.length > 0,
    };
  }, [institutionLicenseSnapshots, institutions]);
  const subscriptionRows = useMemo(() => {
    const now = Date.now();
    return institutions
      .map((institution) => ({
        institution,
        ...subscriptionExpiryView(institution, institutionLicenseSnapshots[institution.id], ar, now),
      }))
      .sort((left, right) => (
        left.sortRank - right.sortRank
        || left.sortAt - right.sortAt
        || String(left.institution.display_name || '').localeCompare(String(right.institution.display_name || ''))
      ));
  }, [ar, institutionLicenseSnapshots, institutions]);

  const load = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      SaaSServices.listInstitutions(),
      SaaSServices.getGlobalMarketplacePolicy(),
      SaaSServices.listMarketplaceReviews(),
      SaaSServices.listGlobalSupportUsers(),
      SaaSServices.listSaaSSupportRequests(),
    ]);
    const responseAt = (index) => results[index]?.status === 'fulfilled' ? results[index].value : null;
    const institutionRes = responseAt(0);
    const marketplaceRes = responseAt(1);
    const listingsRes = responseAt(2);
    const supportUsersRes = responseAt(3);
    const supportRequestsRes = responseAt(4);
    const institutionData = unwrapApiData(institutionRes);
    const rows = Array.isArray(institutionData) ? institutionData : [];
    const errors = {};
    if (!Array.isArray(institutionData)) errors.institutions = apiErrorMessage(institutionRes, 'Institutions could not be loaded');
    if (!unwrapApiData(marketplaceRes)) errors.marketplacePolicy = apiErrorMessage(marketplaceRes, 'Marketplace policy could not be loaded');
    if (!unwrapApiData(listingsRes)) errors.marketplaceListings = apiErrorMessage(listingsRes, 'Marketplace submissions could not be loaded');
    if (!Array.isArray(unwrapApiData(supportUsersRes))) errors.supportUsers = apiErrorMessage(supportUsersRes, 'Support users could not be loaded');
    if (!Array.isArray(unwrapApiData(supportRequestsRes))) errors.supportRequests = apiErrorMessage(supportRequestsRes, 'Support requests could not be loaded');
    setLoadErrors(errors);
    setInstitutions(rows);
    setMarketplacePolicy(unwrapApiData(marketplaceRes) || { sharing_mode: 'disabled', allowed_institution_ids: [], require_superadmin_approval: true, version: 0 });
    setMarketplaceListings(unwrapApiData(listingsRes)?.listings || []);
    setSupportUsers(unwrapApiData(supportUsersRes) || []);
    setSupportRequests(unwrapApiData(supportRequestsRes) || []);
    const licenseResults = await Promise.allSettled(rows.map(async (institution) => {
      const response = await SaaSServices.getInstitutionLicense(institution.id);
      const data = unwrapApiData(response);
      if (!data) throw new Error(apiErrorMessage(response, 'License summary could not be loaded'));
      return [institution.id, data];
    }));
    setInstitutionLicenseSnapshots(Object.fromEntries(licenseResults
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value)));
    if (licenseResults.some((result) => result.status === 'rejected')) {
      setLoadErrors((current) => ({ ...current, licenses: 'Some institution license summaries could not be loaded' }));
    }
    const requestedInstitutionId = String(readSaaSInstitutionSelection() || '').trim();
    const storedInstitutionId = String(readStoredInstitution()?.id || '').trim();
    setSelectedId((old) => rows.find((row) => String(row.id) === requestedInstitutionId)?.id
      || rows.find((row) => String(row.id) === String(old))?.id
      || rows.find((row) => String(row.id) === storedInstitutionId)?.id
      || rows[0]?.id
      || '');
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const syncLanguage = () => setLanguage(localStorage.getItem(SAAS_LANGUAGE_KEY) || 'en');
    window.addEventListener('disasterms:saas-language-changed', syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener('disasterms:saas-language-changed', syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, []);
  useEffect(() => {
    const syncSectionFromUrl = () => {
      const marketplace = isGlobalMarketplaceSearch(window.location.search);
      setGlobalMarketplaceOpen(marketplace);
      setOverviewOpen(!marketplace && !new URLSearchParams(window.location.search).get('institution'));
    };
    const selectSection = (event) => {
      const marketplace = event.detail?.section === SAAS_GLOBAL_MARKETPLACE_SECTION;
      setGlobalMarketplaceOpen(marketplace);
      setOverviewOpen(!marketplace);
    };
    window.addEventListener('popstate', syncSectionFromUrl);
    window.addEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, selectSection);
    return () => {
      window.removeEventListener('popstate', syncSectionFromUrl);
      window.removeEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, selectSection);
    };
  }, []);
  useEffect(() => {
    const selectFromSidebar = (event) => {
      const institutionId = String(event.detail?.institutionId || '').trim();
      if (!institutionId) return;
      setSelectedId(institutionId);
      setTab('institutions');
      setGlobalMarketplaceOpen(false);
      setOverviewOpen(false);
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.delete('tab');
      window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    };
    const selectFromHistory = () => {
      const institutionId = readSaaSInstitutionSelection();
      if (institutionId) {
        setSelectedId(institutionId);
        const requestedTab = new URLSearchParams(window.location.search).get('tab');
        setTab(SAAS_ADMINISTRATION_TABS.some(([id]) => id === requestedTab) ? requestedTab : 'institutions');
      }
      const marketplace = isGlobalMarketplaceSearch(window.location.search);
      setGlobalMarketplaceOpen(marketplace);
      setOverviewOpen(!marketplace && !institutionId);
    };
    window.addEventListener(SAAS_INSTITUTION_SELECTION_EVENT, selectFromSidebar);
    window.addEventListener('popstate', selectFromHistory);
    return () => {
      window.removeEventListener(SAAS_INSTITUTION_SELECTION_EVENT, selectFromSidebar);
      window.removeEventListener('popstate', selectFromHistory);
    };
  }, []);
  useEffect(() => {
    if (selectedId && !globalMarketplaceOpen && !overviewOpen) selectSaaSInstitution(selectedId);
  }, [globalMarketplaceOpen, overviewOpen, selectedId]);
  useEffect(() => {
    let cancelled = false;
    const refreshControlPlane = async () => {
      const [institutionResult, supportResult] = await Promise.allSettled([
        SaaSServices.listInstitutions(),
        SaaSServices.listSaaSSupportRequests(),
      ]);
      if (cancelled) return;
      const institutionResponse = institutionResult.status === 'fulfilled' ? institutionResult.value : null;
      const supportResponse = supportResult.status === 'fulfilled' ? supportResult.value : null;
      const institutionRows = unwrapApiData(institutionResponse);
      if (Array.isArray(institutionRows)) {
        setInstitutions((currentRows) => {
          const selectedDraft = currentRows.find((row) => String(row.id) === String(selectedId));
          return institutionRows.map((row) => (
            selectedDraft && String(row.id) === String(selectedId) ? selectedDraft : row
          ));
        });
      }
      const supportRows = unwrapApiData(supportResponse);
      if (Array.isArray(supportRows)) setSupportRequests(supportRows);
      setLoadErrors((current) => {
        const next = { ...current };
        if (Array.isArray(institutionRows)) delete next.institutions;
        else next.institutions = apiErrorMessage(institutionResponse, 'Institutions could not be refreshed');
        if (Array.isArray(supportRows)) delete next.supportRequests;
        else next.supportRequests = apiErrorMessage(supportResponse, 'Support requests could not be refreshed');
        return next;
      });
    };
    const timer = window.setInterval(refreshControlPlane, 15000);
    const onFocus = () => { refreshControlPlane(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId || overviewOpen || globalMarketplaceOpen) return;
    let cancelled = false;
    setActiveBreakGlass(null);
    Promise.allSettled([
      SaaSServices.getInstitutionLicense(selectedId),
      SaaSServices.listBreakGlassGrants(selectedId),
    ]).then(([licenseResult, grantResult]) => {
      if (cancelled) return;
      const licenseResponse = licenseResult.status === 'fulfilled' ? licenseResult.value : null;
      const grantResponse = grantResult.status === 'fulfilled' ? grantResult.value : null;
      const data = unwrapApiData(licenseResponse);
      if (data) {
        setLicenseForm(toLicenseForm(data.license));
        setSeatUsage(data.seat_usage || {});
        setEffectiveMode(data.effective_access_mode || '');
      }
      const grants = unwrapApiData(grantResponse);
      if (Array.isArray(grants)) setActiveBreakGlass(activeBreakGlassGrant(grants));
      setLoadErrors((current) => {
        const next = { ...current };
        if (data) delete next.selectedLicense;
        else next.selectedLicense = apiErrorMessage(licenseResponse, 'This institution license could not be loaded');
        if (Array.isArray(grants)) delete next.breakGlass;
        else next.breakGlass = apiErrorMessage(grantResponse, 'Emergency access status could not be loaded');
        return next;
      });
    });
    return () => { cancelled = true; };
  }, [globalMarketplaceOpen, overviewOpen, selectedId]);

  const saveInstitution = async (institution, update, successMessage = 'Institution updated') => {
    if (Object.prototype.hasOwnProperty.call(update, 'timezone') && !isValidIanaTimezone(update.timezone)) {
      toast.error('Choose a valid IANA timezone from the list or enter one exactly.');
      return;
    }
    setSaving(true);
    const response = await SaaSServices.updateInstitution(institution.id, update);
    const saved = unwrapApiData(response);
    setSaving(false);
    if (!saved) return toast.error(apiErrorMessage(response));
    setInstitutions((rows) => rows.map((row) => row.id === saved.id ? saved : row));
    window.dispatchEvent(new Event('disasterms:saas-institutions-changed'));
    toast.success(successMessage);
  };

  const saveLicense = async () => {
    setSaving(true);
    let payload;
    try { payload = licensePayload(licenseForm); } catch (_) { setSaving(false); return toast.error('Enter valid license dates.'); }
    const response = await SaaSServices.updateInstitutionLicense(selectedId, payload);
    const saved = unwrapApiData(response);
    setSaving(false);
    if (!saved) return toast.error(apiErrorMessage(response));
    setLicenseForm(toLicenseForm(saved.license));
    setSeatUsage(saved.seat_usage || seatUsage);
    setInstitutionLicenseSnapshots((snapshots) => ({
      ...snapshots,
      [selectedId]: {
        ...(snapshots[selectedId] || {}),
        license: saved.license,
        seat_usage: saved.seat_usage || seatUsage,
        effective_access_mode: saved.effective_access_mode || effectiveMode,
      },
    }));
    toast.success('License updated');
  };

  const validateProvisionStep = (step, { notify = true } = {}) => {
    let message = '';
    if (step === 1) {
      if (!provision.slug.trim() || !provision.display_name.trim() || !provision.legal_name.trim()) {
        message = ar ? 'أكمل معرّف المؤسسة واسم العرض والاسم القانوني.' : 'Complete the institution slug, display name and legal name.';
      } else if (!isValidIanaTimezone(provision.timezone.trim())) {
        message = ar ? 'اختر منطقة زمنية صحيحة من قائمة IANA.' : 'Choose a valid IANA timezone.';
      }
    } else if (step === 2) {
      const email = provision.admin_email.trim();
      if (!email || !/^\S+@\S+\.\S+$/.test(email) || !provision.admin_name.trim() || !provision.admin_password) {
        message = ar ? 'أكمل بريد المسؤول الصحيح واسمه وكلمة المرور المؤقتة.' : 'Complete the administrator email, name and temporary password.';
      } else if (!Number.isFinite(Number(provision.seats)) || Number(provision.seats) < 1) {
        message = ar ? 'يجب أن يكون عدد المقاعد واحدًا على الأقل.' : 'Named-user seats must be at least 1.';
      }
    } else if (step === 3) {
      const radius = Number(provision.operations_area?.radius_km);
      if (!provision.operations_area?.country_iso2 || !Number.isFinite(radius) || radius < 100 || radius > 5000) {
        message = ar ? 'اختر دولة ونطاق مراقبة بين 100 و5,000 كم.' : 'Choose a country and a watch radius between 100 and 5,000 km.';
      }
    }
    if (message && notify) toast.error(message);
    return !message;
  };

  const goToProvisionStep = (nextStep) => {
    if (nextStep <= provisionStep) {
      setProvisionStep(nextStep);
      return;
    }
    for (let step = provisionStep; step < nextStep; step += 1) {
      if (!validateProvisionStep(step)) return;
    }
    setProvisionStep(nextStep);
  };

  const provisionInstitution = async () => {
    if (provisionStep !== 3) return;
    if (![1, 2, 3].every((step) => validateProvisionStep(step))) return;
    const institutionTimezone = provision.timezone.trim();
    if (!isValidIanaTimezone(institutionTimezone)) {
      return toast.error('Enter a valid IANA timezone, such as Asia/Riyadh or UTC.');
    }
    setSaving(true);
    const license = defaultLicense();
    license.licensed_total_seats = Number(provision.seats);
    const response = await SaaSServices.provisionInstitution({
      slug: provision.slug,
      legal_name: provision.legal_name,
      display_name: provision.display_name,
      timezone: institutionTimezone,
      default_language: provision.default_language,
      branding: {},
      incident_numbering_policy: {},
      operations_area: {
        country_iso2: provision.operations_area.country_iso2,
        radius_km: Number(provision.operations_area.radius_km),
      },
      initial_admin: { email: provision.admin_email, full_name: provision.admin_name, password: provision.admin_password },
      license: licensePayload(license),
    });
    const created = unwrapApiData(response);
    setSaving(false);
    if (!created) return toast.error(apiErrorMessage(response));
    setInstitutions((rows) => [...rows, created.institution].sort((a, b) => String(a.display_name).localeCompare(String(b.display_name))));
    setSelectedId(created.institution.id);
    setInstitutionLicenseSnapshots((snapshots) => ({
      ...snapshots,
      [created.institution.id]: {
        license: created.license || license,
        seat_usage: created.seat_usage || { named_users: 1, web_enabled_users: 1 },
        effective_access_mode: created.effective_access_mode || 'full',
      },
    }));
    window.dispatchEvent(new Event('disasterms:saas-institutions-changed'));
    setTab('institutions');
    setOverviewOpen(false);
    setGlobalMarketplaceOpen(false);
    selectSaaSInstitution(created.institution.id);
    setProvisionOpen(false);
    setProvision({
      slug: '', legal_name: '', display_name: '', timezone: 'Asia/Riyadh', default_language: 'en',
      admin_email: '', admin_name: '', admin_password: '', seats: 10,
      operations_area: { country_iso2: '', country_name: '', radius_km: 1250 },
    });
    toast.success('Institution provisioned');
  };

  const saveMarketplacePolicy = async () => {
    setSaving(true);
    const response = await SaaSServices.updateGlobalMarketplacePolicy({
      sharing_mode: marketplacePolicy.sharing_mode,
      allowed_institution_ids: marketplacePolicy.allowed_institution_ids || [],
      require_superadmin_approval: marketplacePolicy.require_superadmin_approval !== false,
      expected_version: marketplacePolicy.version || 0,
    });
    const saved = unwrapApiData(response);
    setSaving(false);
    if (!saved) return toast.error(apiErrorMessage(response));
    setMarketplacePolicy(saved);
    toast.success('Marketplace governance saved');
  };

  const requestMarketplaceReview = (listing, status) => {
    setMarketplaceReviewNote(listing.review_note || '');
    setPendingMarketplaceReview({ listing, status });
  };

  const reviewListing = async () => {
    const pending = pendingMarketplaceReview;
    if (!pending) return;
    const { listing, status } = pending;
    const reviewNote = marketplaceReviewNote.trim();
    setSaving(true);
    const response = await SaaSServices.reviewMarketplaceListing(listing.id, { status, review_note: reviewNote });
    const saved = unwrapApiData(response);
    setSaving(false);
    if (!saved) return toast.error(apiErrorMessage(response));
    setMarketplaceListings((rows) => rows.map((row) => row.id === saved.id ? saved : row));
    setPendingMarketplaceReview(null);
    setMarketplaceReviewNote('');
    toast.success(ar ? 'تم تحديث حالة مشاركة المتجر' : `Marketplace listing ${status}`);
  };

  const requestBreakGlassActivation = (event) => {
    event.preventDefault();
    if (!selectedId) return;
    const expiryError = validateBreakGlassExpiry(
      breakGlassGrant.expires_at,
      new Date(),
      selectedInstitutionTimezone,
    );
    if (expiryError) return toast.error(expiryError);
    setPendingBreakGlassActivation({ ...breakGlassGrant });
  };

  const activateBreakGlass = async () => {
    const requestedGrant = pendingBreakGlassActivation;
    if (!selectedId || !requestedGrant) return;
    setPendingBreakGlassActivation(null);
    const expiresAt = institutionLocalDateTimeToIso(
      requestedGrant.expires_at,
      selectedInstitutionTimezone,
    );
    if (!expiresAt) return toast.error(`Choose a valid time in ${selectedInstitutionTimezone}.`);
    setSaving(true);
    const response = await SaaSServices.createBreakGlassGrant(selectedId, {
      support_user_id: requestedGrant.support_user_id,
      reason: requestedGrant.reason,
      expires_at: expiresAt,
      read_only: false,
    });
    const created = unwrapApiData(response);
    setSaving(false);
    if (!created) return toast.error(apiErrorMessage(response));
    setActiveBreakGlass(created.grant);
    setBreakGlassGrant({ support_user_id: '', reason: '', expires_at: '' });
    toast.success('Break-glass grant activated and visibly disclosed to the institution');
  };

  const assignSupportRequest = async (requestRow) => {
    const supportUserId = String(supportAssignments[requestRow.id] || '').trim();
    if (!supportUserId) return toast.error('Choose a support team member');
    setSaving(true);
    const response = await SaaSServices.assignSaaSSupportRequest(requestRow.id, supportUserId);
    const assigned = unwrapApiData(response);
    setSaving(false);
    if (!assigned) return toast.error(apiErrorMessage(response));
    setSupportRequests((rows) => rows.map((row) => row.id === assigned.id ? assigned : row));
    toast.success('Support request assigned');
  };

  const requestSupportSession = (requestRow, options = {}) => {
    setPendingSupportSession({ requestRow, options });
  };

  const requestCaseClosure = (requestRow) => {
    setCaseResolution('');
    setPendingCaseClosure(requestRow);
  };

  const closeSupportCase = async () => {
    const requestRow = pendingCaseClosure;
    const resolution = caseResolution.trim();
    if (!requestRow || resolution.length < 10) return;
    setSaving(true);
    const response = await SaaSServices.closeSupportCase(requestRow.id, resolution);
    const closed = unwrapApiData(response);
    setSaving(false);
    if (!closed) return toast.error(apiErrorMessage(response, 'Support case could not be closed'));
    setSupportRequests((rows) => rows.map((row) => row.id === closed.id ? closed : row));
    setPendingCaseClosure(null);
    setCaseResolution('');
    toast.success(`Support case ${closed.ticket_number || ''} resolved`);
  };

  const openSupportSession = async (requestRow, { claim = false, breakGlass = false } = {}) => {
    setSaving(true);
    let activeRequest = requestRow;
    if (claim) {
      const claimResponse = await SaaSServices.claimSaaSSupportRequest(requestRow.id);
      activeRequest = unwrapApiData(claimResponse);
      if (!activeRequest) {
        setSaving(false);
        return toast.error(apiErrorMessage(claimResponse));
      }
      setSupportRequests((rows) => rows.map((row) => row.id === activeRequest.id ? activeRequest : row));
    }
    const response = await SaaSServices.startSupportSession(
      activeRequest.institution_id,
      activeRequest.id,
      breakGlass,
    );
    const session = unwrapApiData(response);
    setSaving(false);
    if (!session?.access_token) return toast.error(apiErrorMessage(response, 'Support session could not be started'));
    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete('section');
    returnUrl.searchParams.set('institution', String(activeRequest.institution_id || selectedId));
    returnUrl.searchParams.set('tab', 'support');
    const returnPath = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;
    localStorage.setItem('platform_session_backup', JSON.stringify({
      access_token: localStorage.getItem('access_token'),
      user: currentSaaSUser,
      active_institution: localStorage.getItem('active_institution'),
      return_path: returnPath,
      return_context: {
        institution_id: String(activeRequest.institution_id || selectedId),
        tab: 'support',
        support_request_id: String(activeRequest.id || ''),
      },
    }));
    localStorage.setItem('access_token', session.access_token);
    localStorage.setItem('user', JSON.stringify(session.user));
    localStorage.setItem('active_institution', JSON.stringify({
      id: session.institution_id,
      display_name: activeRequest.institution_name || selectedInstitution?.display_name || 'Institution',
      timezone: session.institution_timezone || activeRequest.institution_timezone || selectedInstitutionTimezone,
    }));
    localStorage.setItem('support_session', JSON.stringify({
      institution_id: session.institution_id,
      institution_name: activeRequest.institution_name || selectedInstitution?.display_name || 'Institution',
      grant_id: session.grant_id,
      expires_at: session.expires_at,
      institution_timezone: session.institution_timezone || activeRequest.institution_timezone || selectedInstitutionTimezone,
      read_only: session.read_only,
      break_glass: session.break_glass,
    }));
    window.location.assign('/dashboard');
  };

  const openInstitutionTab = (nextTab) => {
    setTab(nextTab);
    setGlobalMarketplaceOpen(false);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('section');
    nextUrl.searchParams.set('institution', selectedId);
    nextUrl.searchParams.set('tab', nextTab);
    window.history.replaceState(window.history.state, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  const handleInstitutionTabKeyDown = (event, currentTab) => {
    const tabIds = SAAS_ADMINISTRATION_TABS.map(([id]) => id);
    const currentIndex = tabIds.indexOf(currentTab);
    let nextIndex = currentIndex;
    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabIds.length - 1;
    else if (event.key === 'ArrowRight') nextIndex = (currentIndex + (ar ? -1 : 1) + tabIds.length) % tabIds.length;
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex + (ar ? 1 : -1) + tabIds.length) % tabIds.length;
    else return;
    event.preventDefault();
    const nextTab = tabIds[nextIndex];
    openInstitutionTab(nextTab);
    window.requestAnimationFrame(() => document.getElementById(`saas-tab-${nextTab}`)?.focus());
  };

  if (loading) return <main className="saas-console saas-loading" dir={ar ? 'rtl' : 'ltr'} aria-live="polite">{tr('loading', 'Loading SaaS administration…')}</main>;

  const supportTeam = supportUsers.filter((user) => !user.support_only_session);
  const statusEntries = Object.entries(overviewStats.statuses || {}).filter(([, count]) => count > 0);
  const institutionStatusSummary = statusEntries.length === 1 && statusEntries[0][0] === 'active'
    ? tr('allActive', 'All active')
    : statusEntries.map(([status, count]) => {
      const label = ar ? ({ active: 'نشطة', provisioning: 'قيد الإنشاء', grace: 'فترة سماح', suspended: 'موقوفة', closed: 'مغلقة', unknown: 'غير معروفة' }[status] || status) : status.replace(/_/g, ' ');
      return `${count} ${label}`;
    }).join(' · ') || (ar ? 'لا توجد مؤسسات' : 'No institutions');
  return (
    <main className="saas-console" dir={ar ? 'rtl' : 'ltr'}>
      <div className="saas-content">
        {Object.keys(loadErrors).length > 0 && <section className="saas-modal-notice is-danger" role="status" aria-live="polite">
          <div><b>{tr('partialLoad', 'Some platform data could not be loaded.')}</b><span>{Object.values(loadErrors).join(' · ')}</span></div>
          <button type="button" className="saas-button saas-button-ghost" onClick={load} disabled={loading}>{tr('retry', 'Retry')}</button>
        </section>}
        {overviewOpen && !globalMarketplaceOpen && <div className="saas-page-stack" data-testid="saas-overview-layout">
          <section className="saas-hero">
            <div><p>{tr('platformControl', 'PLATFORM CONTROL PLANE')}</p><h1>{tr('saasTitle', 'DisasterMS SaaS Administration')}</h1><span>{tr('saasDescription', 'Tenant lifecycle, licensing, feature access and marketplace governance.')}</span></div>
            <button type="button" className="saas-button saas-button-hero" onClick={() => { setProvisionStep(1); setProvisionOpen(true); }}><Plus aria-hidden="true" /> {tr('provision', 'Provision')}</button>
          </section>
          <section className="saas-stat-grid" aria-label={tr('summaryLabel', 'SaaS summary')}>
            {[
              ['sky', tr('institutions', 'INSTITUTIONS'), institutions.length, institutionStatusSummary],
              ['green', tr('seatsUsed', 'SEATS USED'), `${overviewStats.seatsUsed}/${overviewStats.seatsLicensed || 0}`, tr('acrossLicenses', 'Across all licenses')],
              ['yellow', tr('licensesExpiring', 'LICENSES EXPIRING'), overviewStats.expiresSoon, tr('withinThirtyDays', 'Within 30 days')],
              ['red', tr('pendingInvitations', 'PENDING INVITATIONS'), overviewStats.invitations ?? '—', overviewStats.invitations === null ? tr('invitationUnavailable', 'Invitation count unavailable') : tr('awaitingActivation', 'Awaiting activation')],
            ].map(([color, label, value, sub]) => <article key={label} className="saas-stat-tile"><p><i className={`is-${color}`} />{label}</p><strong>{value}</strong><span>{sub}</span></article>)}
          </section>
          <section className="saas-overview-grid">
            <article className="saas-overview-card">
              <header className="saas-overview-card-header">
                <div><span className="saas-overview-card-icon is-sky"><Users aria-hidden="true" /></span><div><h2>{tr('seatUsage', 'Seat usage by institution')}</h2><p>{ar ? 'السعة المرخصة عبر جميع المؤسسات' : 'Licensed capacity across all institutions'}</p></div></div>
                <span className={`saas-capacity-insight ${overviewStats.nearCapacityInstitutions > 0 ? 'is-warning' : 'is-healthy'}`} data-testid="saas-capacity-insight">
                  {overviewStats.capacityKnown
                    ? overviewStats.nearCapacityInstitutions > 0
                      ? (ar ? `${overviewStats.nearCapacityInstitutions} عند حد السعة` : `${overviewStats.nearCapacityInstitutions} near limit`)
                      : (ar ? 'السعة بحالة جيدة' : 'Capacity healthy')
                    : (ar ? 'السعة غير متاحة' : 'Capacity unavailable')}
                </span>
              </header>
              <div className="saas-capacity-summary">
                <strong>{overviewStats.capacityKnown ? overviewStats.availableSeats : '—'}</strong>
                <span>{ar ? 'مقعدًا متاحًا على مستوى المنصة' : 'seats available platform-wide'}</span>
              </div>
              <div className="saas-seat-list">
                {institutions.map((institution) => {
                  const snapshot = institutionLicenseSnapshots[institution.id] || {};
                  const used = Number(snapshot.seat_usage?.named_users || 0);
                  const total = Number(snapshot.license?.licensed_total_seats || 0);
                  const pct = total ? Math.min(100, (used / total) * 100) : 0;
                  return <button type="button" key={institution.id} onClick={() => selectSaaSInstitution(institution.id)} aria-label={ar ? `فتح ${institution.display_name}، مستخدم ${used} من ${total} مقعدًا` : `Open ${institution.display_name}, ${used} of ${total} seats used`}><span><b>{institution.display_name}</b><small><em>{Math.round(pct)}%</em>{used}/{total}</small></span><i><b className={pct >= 90 ? 'is-near-limit' : ''} style={{ width: `${pct}%` }} /></i></button>;
                })}
              </div>
            </article>
            <article className="saas-overview-card">
              <header className="saas-overview-card-header">
                <div><span className="saas-overview-card-icon is-green"><CalendarClock aria-hidden="true" /></span><div><h2>{tr('subscriptionRemaining', 'Subscription time remaining')}</h2><p>{ar ? 'تواريخ انتهاء الترخيص حسب المؤسسة' : 'License expiry by institution'}</p></div></div>
                <span className={`saas-expiry-insight ${subscriptionRows.some((row) => row.state === 'expired') ? 'is-danger' : subscriptionRows.some((row) => row.state === 'expiring') ? 'is-warning' : 'is-healthy'}`}>
                  {subscriptionRows.some((row) => row.state === 'expired')
                    ? (ar ? `${subscriptionRows.filter((row) => row.state === 'expired').length} منتهية` : `${subscriptionRows.filter((row) => row.state === 'expired').length} expired`)
                    : subscriptionRows.some((row) => row.state === 'expiring')
                      ? (ar ? `${subscriptionRows.filter((row) => row.state === 'expiring').length} خلال 30 يومًا` : `${subscriptionRows.filter((row) => row.state === 'expiring').length} within 30 days`)
                      : (ar ? 'لا يوجد انتهاء قريب' : 'No expiry within 30 days')}
                </span>
              </header>
              <div className="saas-subscription-list">
                {subscriptionRows.map((row) => <button
                  type="button"
                  key={row.institution.id}
                  className={`is-${row.state}`}
                  onClick={() => selectSaaSInstitution(row.institution.id)}
                  aria-label={ar ? `فتح ترخيص ${row.institution.display_name}: ${row.primary}` : `Open ${row.institution.display_name} license: ${row.primary}`}
                >
                  <span className="saas-subscription-copy">
                    <b>{row.institution.display_name}</b>
                    <small dir="ltr">{row.expiresAt || row.timezone}</small>
                  </span>
                  <span className="saas-subscription-remaining">
                    <strong>{row.primary}</strong>
                    <small>{row.secondary}</small>
                  </span>
                </button>)}
                {subscriptionRows.length === 0 && <div className="saas-overview-empty"><CalendarClock aria-hidden="true" /><span><b>{ar ? 'لا توجد مؤسسات لعرض الاشتراكات.' : 'No institution subscriptions to display.'}</b></span></div>}
              </div>
            </article>
          </section>
        </div>}

        {!overviewOpen && !globalMarketplaceOpen && <div className="saas-page-stack">
          <nav className="saas-institution-tabs" role="tablist" aria-label={tr('administrationSections', 'Institution administration sections')}>
            {SAAS_ADMINISTRATION_TABS.map(([id, label, Icon]) => <button type="button" role="tab" id={`saas-tab-${id}`} aria-selected={tab === id} aria-controls={`saas-panel-${id}`} tabIndex={tab === id ? 0 : -1} key={id} onClick={() => openInstitutionTab(id)} onKeyDown={(event) => handleInstitutionTabKeyDown(event, id)} className={tab === id ? 'is-active' : ''}><Icon aria-hidden="true" />{id === 'institutions' ? tr('institutionTab', label) : id === 'license-features' ? tr('licenseTab', label) : tr('supportTab', label)}</button>)}
          </nav>

          {tab === 'institutions' && <div role="tabpanel" id="saas-panel-institutions" aria-labelledby="saas-tab-institutions" data-testid="saas-institutions-layout">
            {selectedInstitution && <Panel title={tr('institutionLifecycle', 'Institution lifecycle')} description={`DisasterMS ${selectedInstitution.display_name}`} action={<button type="button" className="saas-button saas-button-primary" onClick={() => saveInstitution(selectedInstitution, { display_name: selectedInstitution.display_name, status: selectedInstitution.status, timezone: selectedInstitution.timezone })} disabled={saving}><Save aria-hidden="true" /> {tr('saveChanges', 'Save changes')}</button>}>
              <div className="saas-fields-grid saas-fields-grid-three">
                <label className="saas-field">{tr('displayName', 'Display name')}<Input className="saas-input" value={selectedInstitution.display_name || ''} onChange={(event) => setInstitutions((rows) => rows.map((row) => row.id === selectedId ? { ...row, display_name: event.target.value } : row))} /></label>
                <label className="saas-field">{tr('status', 'Status')}<select className="saas-select" value={selectedInstitution.status} onChange={(event) => setInstitutions((rows) => rows.map((row) => row.id === selectedId ? { ...row, status: event.target.value } : row))}><option value="provisioning">{ar ? 'قيد الإنشاء' : 'Provisioning'}</option><option value="active">{ar ? 'نشطة' : 'Active'}</option><option value="grace">{ar ? 'فترة سماح' : 'Grace'}</option><option value="suspended">{ar ? 'موقوفة' : 'Suspended'}</option><option value="closed">{ar ? 'مغلقة' : 'Closed'}</option></select></label>
                <InstitutionTimezoneField ar={ar} id="institution-timezone" label={tr('timezone', 'Institution timezone')} value={selectedInstitution.timezone || ''} onChange={(timezone) => setInstitutions((rows) => rows.map((row) => row.id === selectedId ? { ...row, timezone } : row))} />
              </div>
              <OperationsAreaFields ar={ar} idPrefix="institution-operations-area" value={operationsAreaFor(selectedInstitution)} onChange={(operationsArea) => setInstitutions((rows) => rows.map((row) => row.id === selectedId ? { ...row, operations_area: operationsArea } : row))} saving={saving} onSave={() => saveInstitution(selectedInstitution, { operations_area: { country_iso2: operationsAreaFor(selectedInstitution).country_iso2, radius_km: Number(operationsAreaFor(selectedInstitution).radius_km) } }, 'Operations Area updated')} />
              <div className="saas-status-definitions" aria-label={ar ? 'تعريفات حالة المؤسسة' : 'Institution status definitions'}>
                <h3>{ar ? 'تعريفات الحالة' : 'Status definitions'}</h3>
                <div>{INSTITUTION_STATUS_DEFINITIONS.map(([status, definition]) => <p key={status}><b>{ar ? ({ Active: 'نشطة', Provisioning: 'قيد الإنشاء', Grace: 'فترة سماح', Suspended: 'موقوفة', Closed: 'مغلقة' }[status] || status) : status}</b><span>{ar ? ({ Active: 'الوصول المرخص المعتاد متاح.', Provisioning: 'لا يزال الإعداد الأولي للمؤسسة جاريًا.', Grace: 'يتبع الوصول المؤقت سلوك فترة السماح في الترخيص.', Suspended: 'وصول المؤسسة محظور حتى تعيده المنصة.', Closed: 'المؤسسة غير نشطة ولا يمكن فتحها.' }[status] || definition) : definition}</span></p>)}</div>
              </div>
            </Panel>}
          </div>}

          {tab === 'license-features' && <div role="tabpanel" id="saas-panel-license-features" aria-labelledby="saas-tab-license-features" data-testid="saas-license-features-layout">{selectedInstitution && <LicenseEditor ar={ar} institution={selectedInstitution} value={licenseForm} seatUsage={seatUsage} effectiveMode={effectiveMode} onChange={setLicenseForm} onSave={saveLicense} saving={saving} />}</div>}

          {tab === 'support' && <div role="tabpanel" id="saas-panel-support" aria-labelledby="saas-tab-support" className="saas-support-stack" data-testid="saas-support-layout">
            {selectedInstitution && <Panel title={tr('supportRequests', 'Platform Support requests')} description={ar ? 'تظهر هنا تلقائيًا الطلبات المعتمدة للقراءة فقط. يفتح الطلب جلسة دعم منفصلة ولا يضيف وصول المؤسسة إلى جلسة إدارة المنصة.' : 'Institution-approved, read-only requests appear here automatically. Opening a request starts a separate support-only session; it never adds tenant access to your SaaS administration session.'}>
              <SectionRule label={tr('requests', 'REQUESTS')} count={selectedSupportRequests.length} />
              <div className="saas-support-list">{selectedSupportRequests.map((requestRow) => {
                const active = supportGrantIsActive(requestRow);
                const closed = Boolean(requestRow.closed_at);
                const assignedToCurrentUser = String(requestRow.support_user_id || '') === currentSaaSUserId;
                const assignedSupportUser = supportUsers.find((user) => String(user.id) === String(requestRow.support_user_id || ''));
                const status = closed ? 'Resolved' : !active ? (requestRow.revoked_at ? 'Revoked' : new Date(requestRow.expires_at).getTime() <= Date.now() ? 'Expired' : 'Unavailable') : requestRow.support_user_id ? 'Assigned' : 'New request';
                const statusLabel = ar ? ({ Resolved: 'تم الحل', Revoked: 'ملغى', Expired: 'منتهي', Unavailable: 'غير متاح', Assigned: 'تم التعيين', 'New request': 'طلب جديد' }[status] || status) : status;
                return <article key={requestRow.id} className="saas-support-row"><div className="saas-support-copy"><div><b dir="ltr">{requestRow.ticket_number}</b><span className={`saas-status is-${status.toLowerCase().replace(/\s/g, '-')}`}>{statusLabel}</span></div><p>{requestRow.reason}</p><small>{requestRow.institution_name || selectedInstitution.display_name} · {ar ? 'ينتهي الوصول' : 'Access ends'} <span dir="ltr">{formatInstitutionDateTime(requestRow.expires_at, requestRow.institution_timezone || selectedInstitutionTimezone)}</span></small>{requestRow.support_user_id && <small>{ar ? 'مُسند إلى' : 'Assigned to'} {assignedSupportUser?.full_name || assignedSupportUser?.email || (assignedToCurrentUser ? (ar ? 'أنت' : 'you') : (ar ? 'عضو فريق الدعم' : 'support team member'))}</small>}{closed && <div className="saas-resolution"><strong>{ar ? 'الحل' : 'RESOLUTION'}</strong><p>{requestRow.resolution || requestRow.resolution_summary}</p><small>{ar ? 'أُغلقت' : 'Closed'} <span dir="ltr">{formatInstitutionDateTime(requestRow.closed_at, requestRow.institution_timezone || selectedInstitutionTimezone)}</span>{requestRow.closed_by_name ? ` ${ar ? 'بواسطة' : 'by'} ${requestRow.closed_by_name}` : ''}</small></div>}</div>
                  {!closed && <div className="saas-support-actions">{active && (!requestRow.support_user_id || assignedToCurrentUser) && <button type="button" className="saas-button saas-button-primary" onClick={() => requestSupportSession(requestRow, { claim: !assignedToCurrentUser })} disabled={saving}>{assignedToCurrentUser ? tr('openReadOnly', 'Open read-only support') : tr('handleReadOnly', 'Handle in read-only mode')}</button>}{active && supportTeam.length > 0 && <div className="saas-assign-row"><select aria-label={ar ? `إسناد طلب الدعم ${requestRow.ticket_number}` : `Assign support request ${requestRow.ticket_number}`} className="saas-select" value={supportAssignments[requestRow.id] || ''} onChange={(event) => setSupportAssignments((old) => ({ ...old, [requestRow.id]: event.target.value }))}><option value="">{ar ? 'إسناد إلى فريق الدعم' : 'Assign to support team'}</option>{supportTeam.map((supportUser) => <option key={supportUser.id} value={supportUser.id}>{supportUser.full_name || supportUser.email}</option>)}</select><button type="button" className="saas-button saas-button-ghost" onClick={() => assignSupportRequest(requestRow)} disabled={saving || !supportAssignments[requestRow.id]}>{ar ? 'إسناد' : 'Assign'}</button></div>}<button type="button" className="saas-button saas-button-ghost" onClick={() => requestCaseClosure(requestRow)} disabled={saving}>{tr('closeCase', 'Close support case')}</button></div>}
                </article>;
              })}{!selectedSupportRequests.length && <div className="saas-empty-state"><HelpCircle aria-hidden="true" /><b>{tr('noRequests', 'No support requests from this institution.')}</b></div>}</div>
            </Panel>}

            {selectedInstitution && <section className="saas-break-glass">
              <header><h2>{tr('emergencySupport', 'Emergency break-glass support')}</h2><p>{ar ? 'يقتصر الوصول الاستثنائي على أربع ساعات، ويُدقق بالكامل ويُبلّغ للمؤسسة بوضوح.' : 'Exceptional access is capped at four hours, fully audited and visibly reported to the institution.'}</p></header>
              <div className="saas-caution"><b>{ar ? 'يُستخدم فقط في طوارئ تشغيلية حقيقية.' : 'Use only for a genuine operational emergency.'}</b><span>{ar ? 'يجب أن يبقى الدعم العادي معتمدًا من المؤسسة وللقراءة فقط. يتجاوز التفويض الطارئ الموافقة العادية فقط عندما يضر التأخير بعمليات الطوارئ.' : 'Ordinary support should remain institution-approved and read-only. Break-glass bypasses that normal approval only when delay would materially harm emergency operations.'}</span></div>
              <SectionRule label={tr('requestDetails', 'REQUEST DETAILS')} danger />
              <form onSubmit={requestBreakGlassActivation} className="saas-emergency-form">
                <div className="saas-field"><label htmlFor="break-glass-user">{ar ? 'من سيتولى حالة الطوارئ؟' : 'Who will handle the emergency?'}</label><select id="break-glass-user" required value={breakGlassGrant.support_user_id} onChange={(event) => setBreakGlassGrant((old) => ({ ...old, support_user_id: event.target.value }))} className="saas-select"><option value="">{ar ? 'اختر مشغّل دعم' : 'Choose a support operator'}</option>{supportUsers.map((supportUser) => <option key={supportUser.id} value={supportUser.id}>{supportUser.support_only_session ? `${supportUser.full_name || supportUser.email} — ${ar ? 'جلسة طوارئ للدعم فقط' : 'support-only emergency session'}` : `${supportUser.full_name || supportUser.email} · ${supportUser.email}`}</option>)}</select></div>
                <div className="saas-reference"><b>{ar ? 'مرجع الطوارئ' : 'Emergency reference'}</b><span>{ar ? 'يُنشأ تلقائيًا عند تفعيل الوصول.' : 'Generated automatically when access is activated.'}</span></div>
                <div className="saas-field"><label htmlFor="break-glass-reason">{ar ? 'سبب الطوارئ' : 'Emergency reason'}</label><Input id="break-glass-reason" className="saas-input" required minLength={10} maxLength={500} placeholder={ar ? 'اشرح لماذا لا يمكن انتظار موافقة الدعم العادية' : 'Explain why normal support approval cannot wait'} value={breakGlassGrant.reason} onChange={(event) => setBreakGlassGrant((old) => ({ ...old, reason: event.target.value }))} /><small>{ar ? '10 أحرف على الأقل؛ يُسجل السبب في سجل التدقيق وإشعار المؤسسة.' : 'Minimum 10 characters; this reason is included in the audit trail and institution notice.'}</small></div>
                <div className="saas-field"><HelpLabel htmlFor="break-glass-expiry" label={ar ? 'وقت الانتهاء' : 'Expiration time'} help={ar ? BREAK_GLASS_HELP_AR.expiry : BREAK_GLASS_HELP.expiry} /><Input id="break-glass-expiry" className="saas-input" required type="datetime-local" min={breakGlassLimits.min} max={breakGlassLimits.max} value={breakGlassGrant.expires_at} onChange={(event) => setBreakGlassGrant((old) => ({ ...old, expires_at: event.target.value }))} /><small>{ar ? 'الحد الأقصى أربع ساعات من التفعيل. المنطقة الزمنية:' : 'Maximum four hours from activation. Timezone:'} <strong dir="ltr">{selectedInstitutionTimezone}</strong>.</small></div>
                <div className="saas-emergency-disclosure"><ShieldAlert aria-hidden="true" /><b>{ar ? 'سيُمنح الوصول التشغيلي للكتابة فورًا مع الإفصاح الواضح.' : 'Emergency operational write access will be granted immediately and visibly disclosed.'}</b><InfoTip label={ar ? 'الوصول التشغيلي الطارئ للكتابة' : 'Emergency operational write access'} content={ar ? BREAK_GLASS_HELP_AR.writeAccess : BREAK_GLASS_HELP.writeAccess} /></div>
                <button type="submit" className="saas-button saas-button-danger" disabled={saving}><ShieldAlert aria-hidden="true" />{tr('activateBreakGlass', 'Activate break-glass')}</button>
              </form>
              {activeBreakGlass && <><SectionRule label={tr('activeAccess', 'ACTIVE ACCESS')} danger /><div className="saas-active-emergency" data-testid="active-break-glass" role="status" aria-live="assertive"><div><b>{ar ? 'وصول الطوارئ نشط' : 'Emergency access active'} · <span dir="ltr">{activeBreakGlass.ticket_number}</span></b><small>{ar ? 'ينتهي' : 'Expires'} <span dir="ltr">{formatInstitutionDateTime(activeBreakGlass.expires_at, activeBreakGlass.institution_timezone || selectedInstitutionTimezone)}</span></small><p>{ar ? 'يمكن للمشغّل المعيّن فتح الجلسة من صندوق الدعم. ينتهي الوصول تلقائيًا أو يمكن إلغاؤه الآن.' : 'The assigned operator can open this from their support inbox. Access ends automatically or can be revoked now.'}</p></div><div>{String(activeBreakGlass.support_user_id || '') === currentSaaSUserId && <button type="button" className="saas-button saas-button-danger-dark" onClick={() => requestSupportSession({ ...activeBreakGlass, institution_name: selectedInstitution.display_name }, { breakGlass: true })} disabled={saving}>{tr('startEmergency', 'Start emergency session')}</button>}<button type="button" className="saas-button saas-button-danger-outline" onClick={async () => { const response = await SaaSServices.revokeBreakGlassGrant(selectedId, activeBreakGlass.id); if (unwrapApiData(response)) { setActiveBreakGlass(null); toast.success(ar ? 'أُلغي التفويض الطارئ' : 'Break-glass grant revoked'); } else toast.error(apiErrorMessage(response)); }}>{tr('revokeNow', 'Revoke now')}</button></div></div></>}
            </section>}
          </div>}
        </div>}

        {globalMarketplaceOpen && <div className="saas-page-stack" data-testid="saas-marketplace-layout">
          {!marketplaceGuideDismissed && <section className="saas-panel saas-marketplace-guide">
            <button
              type="button"
              className="saas-marketplace-guide-dismiss"
              aria-label={ar ? 'إخفاء شرح المتجر لهذه الجلسة' : 'Dismiss Marketplace guide for this session'}
              onClick={() => {
                setMarketplaceGuideDismissed(true);
                dismissMarketplaceGuide();
              }}
            >
              <X aria-hidden="true" />
            </button>
            <div className="saas-marketplace-heading"><h2>{tr('marketplaceSharing', 'How Marketplace sharing works')}</h2><p>{ar ? 'مكتبة محكومة لإعادة استخدام قوالب السيناريوهات المعتمدة بين المؤسسات.' : 'A controlled library for reusing approved scenario templates across institutions.'}</p></div>
            <div className="saas-marketplace-pipeline" aria-label={ar ? 'مراحل مشاركة المتجر' : 'Marketplace sharing stages'}>{[
              ['sky', Upload, ar ? 'إرسال' : 'Submit', ar ? 'ترسل المؤسسة لقطة قابلة لإعادة الاستخدام من قالب السيناريو.' : 'An institution submits a reusable scenario template snapshot.'],
              ['yellow', Check, ar ? 'مراجعة' : 'Review', ar ? 'يعتمد فريق المنصة النشر أو يرفضه أو يوقفه.' : 'The SaaS team approves, rejects or suspends publication.'],
              ['green', Search, ar ? 'اكتشاف' : 'Discover', ar ? 'تستعرض المؤسسات المؤهلة القوالب المعتمدة فقط.' : 'Eligible institutions browse only approved templates.'],
              ['red', ArrowDown, ar ? 'استيراد' : 'Import', ar ? 'تحصل المؤسسة المستلمة على مسودة خاصة مستقلة.' : 'The receiving institution gets a separate private draft.'],
            ].map(([color, Icon, title, body]) => <article key={title} className={`is-${color}`}><span><Icon aria-hidden="true" /></span><b>{title}</b><p>{body}</p></article>)}</div>
            <div className="saas-marketplace-safety"><span><ShieldCheck aria-hidden="true" /></span><p><strong>{ar ? 'تُشارك فقط لقطات قوالب السيناريوهات القابلة لإعادة الاستخدام.' : 'Only reusable scenario template snapshots are shared.'}</strong> {ar ? 'لا تُنشر الحوادث المباشرة أو المستخدمون أو المحادثات أو التقارير أو المرفقات في المتجر.' : 'Live incidents, users, chats, reports and attachments are never published to the Marketplace.'}</p></div>
            <div className="saas-workflow">
              <button type="button" className="saas-workflow-toggle" onClick={() => setWorkflowOpen((open) => !open)} aria-expanded={workflowOpen} aria-controls="saas-marketplace-workflow"><span><BookOpen aria-hidden="true" /></span><div><b>{tr('workflow', 'Exact Marketplace workflow')}</b><p>{ar ? 'دليل مرجعي لدورة حياة قالب السيناريو المشترك من البداية إلى النهاية.' : 'Reference guide — the full lifecycle of a shared scenario template, end to end.'}</p></div><em>{ar ? '8 مراحل' : '8 STAGES'}</em><ChevronDown className={workflowOpen ? 'is-open' : ''} aria-hidden="true" /></button>
              {workflowOpen && <div id="saas-marketplace-workflow" role="region" className="saas-workflow-timeline">{WORKFLOW_STAGES.map((stage, index) => <article key={stage.title} className={`is-${stage.color}`}><span className="saas-stage-number">{index + 1}</span><div className="saas-stage-card"><header><b>{ar ? ARABIC_WORKFLOW_TITLES[index] : stage.title}</b><em>{ar ? ARABIC_WORKFLOW_ACTORS[index] : stage.actor}</em></header><p>{ar ? ARABIC_WORKFLOW_BODIES[index] : stage.body}</p>{stage.removed && <div className="saas-chip-list is-removed">{(ar ? ARABIC_WORKFLOW_REMOVED : stage.removed).map((item) => <span key={item}>× {item}</span>)}</div>}{stage.added && <div className="saas-chip-list is-added">{(ar ? ARABIC_WORKFLOW_ADDED : stage.added).map((item) => <span key={item}>✓ {item}</span>)}</div>}{stage.note && <small>{ar ? ARABIC_WORKFLOW_NOTES[index] : stage.note}</small>}</div></article>)}</div>}
            </div>
          </section>}

          <section className="saas-marketplace-stats" aria-label={tr('marketplaceSummary', 'Marketplace summary')}>{[
            ['sky', ar ? 'المؤسسات المسموح لها' : 'Institutions allowed', marketplaceAllowedCount], ['yellow', ar ? 'بانتظار المراجعة' : 'Awaiting review', marketplaceSummary.pending], ['green', ar ? 'منشورة' : 'Published', marketplaceSummary.published], ['red', ar ? 'مرفوضة أو موقوفة' : 'Rejected or suspended', marketplaceSummary.paused],
          ].map(([color, label, value]) => <article key={label} className={`is-${color}`}><span>{label}</span><strong>{value}</strong></article>)}</section>

          <Panel title={tr('marketplacePolicy', 'Marketplace access policy')} description={ar ? 'اختر المؤسسات المرخصة التي يمكنها المشاركة. يبقى الوصول إلى المتجر معطلًا ما لم تكن ميزة متجر السيناريوهات مفعلة للمؤسسة.' : 'Choose which licensed institutions may participate. Marketplace access remains disabled unless the institution also has its Scenario marketplace feature enabled.'} action={<button type="button" className="saas-button saas-button-primary" onClick={saveMarketplacePolicy} disabled={saving}><Save aria-hidden="true" />{tr('savePolicy', 'Save policy')}</button>}>
            <SectionRule label={tr('policySettings', 'POLICY SETTINGS')} />
            <div className="saas-policy-grid"><label className="saas-field">{ar ? 'من يمكنه استخدام المتجر؟' : 'Who may use the Marketplace?'}<select value={marketplacePolicy.sharing_mode} onChange={(event) => setMarketplacePolicy((old) => ({ ...old, sharing_mode: event.target.value }))} className="saas-select"><option value="disabled">{ar ? 'لا توجد مؤسسات — اجعل كل القوالب خاصة' : 'No institutions — keep every template private'}</option><option value="allowlist">{ar ? 'المؤسسات المحددة فقط' : 'Only selected institutions'}</option><option value="all">{ar ? 'جميع المؤسسات المرخصة' : 'All licensed institutions'}</option></select><small>{ar ? 'يتحكم هذا في أهلية المتجر عالميًا؛ وتظل كل مؤسسة بحاجة إلى ترخيص الميزة.' : 'This controls Marketplace eligibility globally; each institution still needs its own feature entitlement.'}</small></label><label className="saas-feature-tile saas-policy-toggle"><span><b>{ar ? 'المراجعة قبل النشر' : 'Review before publication'}</b><small>{ar ? 'أبقِ هذا مفعّلًا حتى تظل القوالب خاصة إلى أن يعتمدها مسؤول المنصة.' : 'Keep this on so submitted templates stay private until a SaaS administrator approves them.'}</small></span><Switch className="saas-switch" aria-label={ar ? 'اشتراط موافقة مسؤول المنصة قبل النشر' : 'Require SaaS approval before publication'} checked={marketplacePolicy.require_superadmin_approval !== false} onCheckedChange={(value) => setMarketplacePolicy((old) => ({ ...old, require_superadmin_approval: value }))} /></label></div>
            {marketplacePolicy.sharing_mode === 'allowlist' && <div className="saas-allowlist"><div><b>{ar ? 'المؤسسات المسموح لها' : 'Allowed institutions'}</b><small>{(marketplacePolicy.allowed_institution_ids || []).length} {ar ? 'محددة. لا يمكن للمؤسسات غير المحددة النشر أو التصفح أو الاستيراد.' : 'selected. Unchecked institutions cannot publish, browse or import Marketplace templates.'}</small><Input aria-label={ar ? 'البحث في المؤسسات لوصول المتجر' : 'Search institutions for Marketplace access'} className="saas-input" placeholder={ar ? 'ابحث في المؤسسات' : 'Search institutions'} value={marketplaceInstitutionSearch} onChange={(event) => setMarketplaceInstitutionSearch(event.target.value)} /></div><section>{filteredMarketplaceInstitutions.map((institution) => { const checked = (marketplacePolicy.allowed_institution_ids || []).includes(institution.id); return <label key={institution.id} className={checked ? 'is-selected' : ''}><input type="checkbox" checked={checked} onChange={() => setMarketplacePolicy((old) => ({ ...old, allowed_institution_ids: checked ? old.allowed_institution_ids.filter((id) => id !== institution.id) : [...(old.allowed_institution_ids || []), institution.id] }))} /><span><b>{institution.display_name}</b><small>{institution.legal_name}</small></span></label>; })}</section>{!filteredMarketplaceInstitutions.length && <p>{ar ? 'لا توجد مؤسسات تطابق البحث.' : 'No institutions match this search.'}</p>}</div>}
          </Panel>

          <Panel title={tr('publicationReview', 'Publication review')} description={ar ? 'راجع لقطات قوالب السيناريوهات قبل إتاحتها للاكتشاف.' : 'Review submitted scenario template snapshots before they become discoverable.'}>
            <SectionRule label={tr('submissions', 'SUBMISSIONS')} count={marketplaceListings.length} />
            <div className="saas-submission-filters"><label className="saas-field">{ar ? 'البحث في المشاركات' : 'Search submissions'}<Input aria-label={ar ? 'البحث في مشاركات المتجر' : 'Search Marketplace submissions'} className="saas-input" placeholder={ar ? 'القالب أو المؤسسة أو الملاحظة' : 'Template, institution or note'} value={marketplaceSearch} onChange={(event) => setMarketplaceSearch(event.target.value)} /></label><label className="saas-field">{tr('status', 'Status')}<select aria-label={ar ? 'تصفية مشاركات المتجر حسب الحالة' : 'Filter Marketplace submissions by status'} value={marketplaceStatus} onChange={(event) => setMarketplaceStatus(event.target.value)} className="saas-select"><option value="all">{ar ? 'جميع الحالات' : 'All statuses'}</option><option value="pending">{ar ? 'بانتظار المراجعة' : 'Awaiting review'}</option><option value="published">{ar ? 'منشورة' : 'Published'}</option><option value="rejected">{ar ? 'مرفوضة' : 'Rejected'}</option><option value="suspended">{ar ? 'موقوفة' : 'Suspended'}</option></select></label></div>
            <p className="saas-showing">{ar ? `عرض ${filteredMarketplaceListings.length} من ${marketplaceListings.length} مشاركة.` : `Showing ${filteredMarketplaceListings.length} of ${marketplaceListings.length} submissions.`}</p>
            <div className="saas-submission-list">{filteredMarketplaceListings.map((listing) => { const status = String(listing.status || '').toLowerCase(); const statusLabel = ar ? ({ pending: 'بانتظار المراجعة', published: 'منشورة', rejected: 'مرفوضة', suspended: 'موقوفة', revoked: 'مسحوبة' }[status] || status) : status === 'pending' ? 'Awaiting review' : status; return <article key={listing.id}><div><header><b>{listing.name || (ar ? 'قالب سيناريو بلا عنوان' : 'Untitled scenario template')}</b><span className={`saas-status is-${status}`}>{statusLabel}</span></header><p>{ar ? 'أرسلت بواسطة' : 'Submitted by'} <strong>{listing.source_institution_name || (ar ? 'مؤسسة غير معروفة' : 'Unknown institution')}</strong></p><small>{listing.publisher_note || (ar ? 'لم تُضف ملاحظة من الناشر.' : 'No publisher note was provided.')}</small>{listing.review_note && <blockquote><strong>{ar ? 'آخر ملاحظة مراجعة:' : 'Latest review note:'}</strong> {listing.review_note}</blockquote>}</div><aside>{status === 'pending' && <><button type="button" className="saas-button saas-button-primary" onClick={() => requestMarketplaceReview(listing, 'published')}>{ar ? 'اعتماد' : 'Approve'}</button><button type="button" className="saas-button saas-button-ghost" onClick={() => requestMarketplaceReview(listing, 'rejected')}>{ar ? 'رفض' : 'Reject'}</button></>}{status === 'published' && <button type="button" className="saas-button saas-button-ghost" onClick={() => requestMarketplaceReview(listing, 'suspended')}>{ar ? 'إيقاف' : 'Suspend'}</button>}{status === 'suspended' && <button type="button" className="saas-button saas-button-primary" onClick={() => requestMarketplaceReview(listing, 'published')}>{ar ? 'إعادة النشر' : 'Republish'}</button>}{['rejected', 'revoked'].includes(status) && <small>{ar ? 'يجب على المؤسسة المصدر إرسال إصدار جديد.' : 'The source institution must submit a new version.'}</small>}</aside></article>; })}{!marketplaceListings.length && <div className="saas-empty-state"><Store aria-hidden="true" /><b>{ar ? 'لم تُرسل أي قوالب سيناريوهات.' : 'No scenario templates have been submitted.'}</b><span>{ar ? 'ستظهر المشاركات الجديدة هنا لمراجعة النشر.' : 'New submissions will appear here for publication review.'}</span></div>}{marketplaceListings.length > 0 && !filteredMarketplaceListings.length && <div className="saas-empty-state"><Search aria-hidden="true" /><b>{ar ? 'لا توجد مشاركات تطابق هذه المرشحات.' : 'No submissions match these filters.'}</b><button type="button" onClick={() => { setMarketplaceSearch(''); setMarketplaceStatus('all'); }}>{ar ? 'مسح المرشحات' : 'Clear filters'}</button></div>}</div>
          </Panel>
        </div>}
      </div>

      <Dialog open={Boolean(pendingMarketplaceReview)} onOpenChange={(open) => { if (!open && !saving) { setPendingMarketplaceReview(null); setMarketplaceReviewNote(''); } }}>
        <DialogContent className="saas-dialog max-w-lg" dir={ar ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{ar ? ({ published: 'تأكيد اعتماد النشر', rejected: 'تأكيد رفض النشر', suspended: 'تأكيد إيقاف النشر' }[pendingMarketplaceReview?.status] || 'مراجعة النشر') : ({ published: 'Approve Marketplace publication?', rejected: 'Reject Marketplace publication?', suspended: 'Suspend Marketplace publication?' }[pendingMarketplaceReview?.status] || 'Review Marketplace publication')}</DialogTitle>
            <DialogDescription>{ar ? `سجّل نتيجة مراجعة ${pendingMarketplaceReview?.listing?.name || 'قالب السيناريو'}.` : `Record the review outcome for ${pendingMarketplaceReview?.listing?.name || 'this scenario template'}.`}</DialogDescription>
          </DialogHeader>
          <label htmlFor="marketplace-review-note" className="saas-field">
            {ar ? 'ملاحظة المراجعة (اختيارية)' : 'Review note (optional)'}
            <textarea
              id="marketplace-review-note"
              className="saas-textarea"
              maxLength={2000}
              placeholder={ar ? 'أضف سبب القرار أو تعليمات للمؤسسة الناشرة' : 'Add the reason for this decision or guidance for the publishing institution'}
              value={marketplaceReviewNote}
              onChange={(event) => setMarketplaceReviewNote(event.target.value)}
            />
          </label>
          <DialogFooter>
            <button type="button" className="saas-button saas-button-ghost" onClick={() => { setPendingMarketplaceReview(null); setMarketplaceReviewNote(''); }} disabled={saving}>{tr('cancel', 'Cancel')}</button>
            <button type="button" className={`saas-button ${pendingMarketplaceReview?.status === 'published' ? 'saas-button-primary' : 'saas-button-danger-dark'}`} onClick={reviewListing} disabled={saving}>
              {saving ? (ar ? 'جارٍ الحفظ…' : 'Saving…') : ar ? ({ published: 'اعتماد النشر', rejected: 'رفض النشر', suspended: 'إيقاف النشر' }[pendingMarketplaceReview?.status] || 'حفظ المراجعة') : ({ published: 'Approve publication', rejected: 'Reject publication', suspended: 'Suspend publication' }[pendingMarketplaceReview?.status] || 'Save review')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={provisionOpen} onOpenChange={(open) => { if (!saving) { setProvisionOpen(open); if (open) setProvisionStep(1); } }}>
        <DialogContent className="saas-dialog saas-provision-dialog" dir={ar ? 'rtl' : 'ltr'}>
          <DialogHeader className="saas-dialog-header">
            <DialogTitle>{tr('provisionInstitution', 'Provision a new institution')}</DialogTitle>
            <DialogDescription>{ar ? 'ينشئ المؤسسة وعضوية مسؤولها الأول وترخيص مقاعد المستخدمين الأول كوحدة واحدة.' : 'Creates the tenant, its initial administrator membership and its first unified named-user license.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => {
            event.preventDefault();
            if (provisionStep < 3) goToProvisionStep(provisionStep + 1);
          }}>
            <div className="saas-provision-body">
              <div className="saas-step-banner">
                <span>{provisionStep === 1 ? <Building2 /> : provisionStep === 2 ? <Users /> : <Globe />}</span>
                <div><small>{ar ? `الخطوة ${provisionStep} من 3` : `STEP ${provisionStep} OF 3`}</small><b>{provisionStep === 1 ? (ar ? 'المؤسسة' : 'Institution') : provisionStep === 2 ? (ar ? 'المسؤول والترخيص' : 'Administrator & License') : (ar ? 'منطقة العمليات' : 'Operations Area')}</b></div>
              </div>
              <nav className="saas-step-indicator" aria-label={tr('provisioningSteps', 'Provisioning steps')}>
                {[
                  {
                    label: ar ? 'المؤسسة' : 'Institution',
                    description: ar ? 'الهوية والإعدادات الإقليمية' : 'Identity and regional settings',
                  },
                  {
                    label: ar ? 'المسؤول والترخيص' : 'Administrator & License',
                    description: ar ? 'الحساب الأول والسعة' : 'First account and capacity',
                  },
                  {
                    label: ar ? 'منطقة العمليات' : 'Operations Area',
                    description: ar ? 'الدولة ونطاق المراقبة' : 'Country and watch radius',
                  },
                ].map(({ label, description }, index) => {
                  const stepNumber = index + 1;
                  return <React.Fragment key={label}><button type="button" aria-current={provisionStep === stepNumber ? 'step' : undefined} aria-controls={`provision-step-${stepNumber}`} className={provisionStep === stepNumber ? 'is-current' : provisionStep > stepNumber ? 'is-done' : ''} onClick={() => goToProvisionStep(stepNumber)}><span className="saas-step-node">{provisionStep > stepNumber ? <Check aria-hidden="true" /> : stepNumber}</span><span className="saas-step-label"><b>{label}</b><small>{description}</small></span></button>{stepNumber < 3 && <i aria-hidden="true" className={provisionStep > stepNumber ? 'is-done' : ''} />}</React.Fragment>;
                })}
              </nav>
              <div className="saas-step-content" id={`provision-step-${provisionStep}`} role="group" aria-label={ar ? `الخطوة ${provisionStep}` : `Provisioning step ${provisionStep}`}>
                {provisionStep === 1 && <div className="saas-provision-grid">
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-slug">{ar ? 'معرّف المؤسسة' : 'INSTITUTION SLUG'}</label><Input id="provision-slug" className="saas-input" required autoCapitalize="none" spellCheck={false} placeholder={ar ? 'حروف إنجليزية صغيرة وأرقام وشرطات فقط' : 'Lowercase letters, numbers and hyphens only'} value={provision.slug} onChange={(e) => setProvision((old) => ({ ...old, slug: e.target.value }))} /></div>
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-display-name">{ar ? 'اسم العرض' : 'DISPLAY NAME'}</label><Input id="provision-display-name" className="saas-input" required placeholder={ar ? 'اسم عرض المؤسسة' : 'Institution display name'} value={provision.display_name} onChange={(e) => setProvision((old) => ({ ...old, display_name: e.target.value }))} /></div>
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-legal-name">{ar ? 'الاسم القانوني' : 'LEGAL NAME'}</label><Input id="provision-legal-name" className="saas-input" required placeholder={ar ? 'الاسم القانوني المسجل' : 'Registered legal name'} value={provision.legal_name} onChange={(e) => setProvision((old) => ({ ...old, legal_name: e.target.value }))} /></div>
                  <InstitutionTimezoneField ar={ar} id="provision-timezone" label={ar ? 'المنطقة الزمنية للمؤسسة' : 'INSTITUTION TIMEZONE'} value={provision.timezone} onChange={(timezone) => setProvision((old) => ({ ...old, timezone }))} />
                </div>}
                {provisionStep === 2 && <div className="saas-provision-grid">
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-admin-email">{ar ? 'بريد المسؤول الأول' : 'INITIAL ADMINISTRATOR EMAIL'}</label><Input id="provision-admin-email" className="saas-input" required type="email" placeholder="admin@institution.org" value={provision.admin_email} onChange={(e) => setProvision((old) => ({ ...old, admin_email: e.target.value }))} /></div>
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-admin-name">{ar ? 'اسم المسؤول الأول' : 'INITIAL ADMINISTRATOR NAME'}</label><Input id="provision-admin-name" className="saas-input" required placeholder={ar ? 'الاسم الكامل' : 'Full name'} value={provision.admin_name} onChange={(e) => setProvision((old) => ({ ...old, admin_name: e.target.value }))} /></div>
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-admin-password">{ar ? 'كلمة المرور المؤقتة' : 'TEMPORARY PASSWORD'}</label><Input id="provision-admin-password" className="saas-input" required type="password" placeholder={ar ? 'كلمة مرور مؤقتة' : 'Temporary password'} value={provision.admin_password} onChange={(e) => setProvision((old) => ({ ...old, admin_password: e.target.value }))} /></div>
                  <div className="saas-field saas-field-caps"><label htmlFor="provision-seats">{ar ? 'مقاعد المستخدمين' : 'NAMED-USER SEATS'}</label><Input id="provision-seats" className="saas-input" required type="number" min="1" aria-label={ar ? 'مقاعد المستخدمين' : 'Named-user seats'} value={provision.seats} onChange={(e) => setProvision((old) => ({ ...old, seats: e.target.value }))} /></div>
                </div>}
                {provisionStep === 3 && <OperationsAreaFields ar={ar} idPrefix="provision-operations-area" value={provision.operations_area} onChange={(operationsArea) => setProvision((old) => ({ ...old, operations_area: operationsArea }))} />}
              </div>
            </div>
            <DialogFooter className="saas-provision-footer">
              <button type="button" className="saas-cancel-button" onClick={() => setProvisionOpen(false)} disabled={saving}>{tr('cancel', 'Cancel')}</button>
              <span />
              {provisionStep > 1 && <button type="button" className="saas-button saas-button-ghost" onClick={() => goToProvisionStep(Math.max(1, provisionStep - 1))}><ArrowLeft aria-hidden="true" />{tr('back', 'Back')}</button>}
              {provisionStep < 3 ? <button type="button" className="saas-button saas-button-primary" onClick={() => goToProvisionStep(Math.min(3, provisionStep + 1))}>{tr('next', 'Next')}<ArrowRight aria-hidden="true" /></button> : <button type="button" className="saas-button saas-button-danger" onClick={provisionInstitution} disabled={saving || !provisionOperationsValid}><Plus aria-hidden="true" />{saving ? (ar ? 'جارٍ الإنشاء…' : 'Provisioning…') : tr('provisionInstitution', 'Provision institution')}</button>}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingSupportSession)} onOpenChange={(open) => { if (!open && !saving) setPendingSupportSession(null); }}>
        <DialogContent className="saas-dialog max-w-lg" dir={ar ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{pendingSupportSession?.options?.breakGlass ? (ar ? 'بدء جلسة دعم طارئة؟' : 'Start emergency support session?') : (ar ? 'فتح دعم للقراءة فقط؟' : 'Open read-only support?')}</DialogTitle>
            <DialogDescription>
              {pendingSupportSession?.options?.breakGlass
                ? ar ? `ستدخل ${pendingSupportSession?.requestRow?.institution_name || selectedInstitution?.display_name || 'هذه المؤسسة'} بصلاحيات الطوارئ المفصح عنها فقط.` : `You are about to enter ${pendingSupportSession?.requestRow?.institution_name || selectedInstitution?.display_name || 'this institution'} with only the disclosed emergency permissions.`
                : ar ? `ستفتح ${pendingSupportSession?.requestRow?.institution_name || selectedInstitution?.display_name || 'هذه المؤسسة'} بوضع دعم للقراءة فقط.` : `You are about to open ${pendingSupportSession?.requestRow?.institution_name || selectedInstitution?.display_name || 'this institution'} in read-only support mode.`}
            </DialogDescription>
          </DialogHeader>
          <div className={`saas-modal-notice ${pendingSupportSession?.options?.breakGlass ? 'is-danger' : 'is-info'}`}>
            {pendingSupportSession?.options?.breakGlass
              ? ar ? 'تستخدم هذه الجلسة المؤقتة عمليات الطوارئ المحدودة المفصح عنها للمؤسسة فقط. ولا يمكنها إدارة الترخيص أو المستخدمين أو إعدادات المتجر أو مكتبة السيناريوهات.' : 'This temporary session can use only the limited emergency operations already disclosed to the institution. It cannot manage licensing, users, marketplace settings or the scenario library.'
              : ar ? 'يمكن لهذه الجلسة مراجعة المؤسسة فقط. تُحظر التغييرات ويُدقق النشاط وينتهي الوصول تلقائيًا.' : 'This session can review the institution only. Changes are blocked, activity is audited and access ends automatically.'}
          </div>
          <p className="saas-modal-footnote">{ar ? 'ستُحفظ جلسة إدارة المنصة وسياق المؤسسة والدعم لتعود إليها بعد انتهاء العمل.' : 'Your SaaS Administration session and institution/support context will be saved so you can return when support work is finished.'}</p>
          <DialogFooter>
            <button type="button" className="saas-button saas-button-ghost" onClick={() => setPendingSupportSession(null)} disabled={saving}>{tr('cancel', 'Cancel')}</button>
            <button
              type="button"
              className={`saas-button ${pendingSupportSession?.options?.breakGlass ? 'saas-button-danger-dark' : 'saas-button-primary'}`}
              disabled={saving}
              onClick={() => {
                const pending = pendingSupportSession;
                setPendingSupportSession(null);
                if (pending) openSupportSession(pending.requestRow, pending.options);
              }}
            >
              {saving ? (ar ? 'جارٍ الفتح…' : 'Opening…') : pendingSupportSession?.options?.breakGlass ? (ar ? 'متابعة إلى الدعم الطارئ' : 'Continue to emergency support') : (ar ? 'متابعة بوضع القراءة فقط' : 'Continue in read-only mode')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingCaseClosure)} onOpenChange={(open) => { if (!open && !saving) { setPendingCaseClosure(null); setCaseResolution(''); } }}>
        <DialogContent className="saas-dialog max-w-lg" dir={ar ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{ar ? 'إغلاق حالة الدعم؟' : 'Close support case?'}</DialogTitle>
            <DialogDescription>
              {ar ? 'سجّل النتيجة للطلب' : 'Record the outcome for'} <span dir="ltr">{pendingCaseClosure?.ticket_number || (ar ? 'هذا الطلب' : 'this request')}</span>. {ar ? 'يؤدي إغلاق الحالة إلى إنهاء أي جلسة دعم نشطة فورًا.' : 'Closing the case immediately ends any active support session.'}
            </DialogDescription>
          </DialogHeader>
          <label htmlFor="support-case-resolution" className="saas-field">
            {ar ? 'ملخص الحل' : 'Resolution summary'}
            <textarea
              id="support-case-resolution"
              className="saas-textarea"
              required
              minLength={10}
              maxLength={2000}
              placeholder={ar ? 'اشرح ما تم فحصه وما تم إصلاحه والنتيجة النهائية' : 'Explain what was checked, what was fixed and the final outcome'}
              value={caseResolution}
              onChange={(event) => setCaseResolution(event.target.value)}
            />
            <span className="mt-1 block text-xs font-normal text-slate-500">{ar ? 'يبقى هذا الملخص ظاهرًا للمؤسسة وفي سجل تدقيق الدعم.' : 'This summary remains visible to the institution and in the support audit history.'}</span>
          </label>
          <DialogFooter>
            <button type="button" className="saas-button saas-button-ghost" onClick={() => { setPendingCaseClosure(null); setCaseResolution(''); }} disabled={saving}>{ar ? 'إبقاء الحالة مفتوحة' : 'Keep case open'}</button>
            <button type="button" className="saas-button saas-button-primary" onClick={closeSupportCase} disabled={saving || caseResolution.trim().length < 10}>{saving ? (ar ? 'جارٍ الإغلاق…' : 'Closing…') : (ar ? 'إغلاق كمحلولة' : 'Close as resolved')}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(pendingBreakGlassActivation)} onOpenChange={(open) => { if (!open && !saving) setPendingBreakGlassActivation(null); }}>
        <DialogContent className="saas-dialog max-w-lg" dir={ar ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle>{ar ? 'تفعيل الوصول الطارئ؟' : 'Activate emergency access?'}</DialogTitle>
            <DialogDescription>
              {ar ? 'سيحصل مشغّل الدعم المحدد على صلاحيات الطوارئ المفصح عنها لهذه المؤسسة فقط. لا تبدأ جلسة الدعم تلقائيًا.' : 'The selected support operator will receive only the disclosed emergency permissions for this institution. The support session will not start automatically.'}
            </DialogDescription>
          </DialogHeader>
          <div className="saas-modal-notice is-danger">
            {ar ? 'ينشئ التفعيل إشعارًا حرجًا للمؤسسة فورًا، ويسجل الإجراء في سجل التدقيق ويبدأ وقت الانتهاء المحدد.' : 'Activating break-glass immediately creates a critical institution notice, records the action in the audit trail and starts the configured expiration time.'}
          </div>
          <DialogFooter>
            <button type="button" className="saas-button saas-button-ghost" onClick={() => setPendingBreakGlassActivation(null)} disabled={saving}>{tr('cancel', 'Cancel')}</button>
            <button type="button" className="saas-button saas-button-danger-dark" onClick={activateBreakGlass} disabled={saving}>
              {ar ? 'تفعيل الوصول الطارئ' : 'Activate emergency access'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
